import { Box, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import MultiQtyCell from "../../components/dms_common/MultiQtyCell";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { viewManageOrderRowsData } from "./ViewOrderType";
import { ReactComponent as CircledRight } from "../../assets/image/svg/circledRight.svg";
import { UOM } from "../createSecondaryOrder/CreateSecondaryOrderType";
import { ReactComponent as DisclaimerIcon } from "../../assets/image/svg/disclaimer.svg";
import { ReactComponent as EncircledDisclaimerIcon } from "../../assets/image/svg/encircledDisclaimer.svg";
import { calculateFinalStock, ordersWithStockExceedingWarning } from "./ViewOrderUtils";
import { ReactComponent as SubtractIcon } from "../../assets/image/svg/subtractIcon.svg";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { TruncatedTextUptoLine, truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import { performUomConversion } from "../../services/catalogueService";
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { ColumnsConfiguration } from "./ViewOrderV2";

let currency: string = "",
  caseUnitLabel: string = "",
  otherUnitLabel: string = "",
  packUnitLabel: string = "";

export const setAndGetViewOrderEditTableHeader = (ConfigDictionary: any, primaryOrder: boolean = false) => {
  caseUnitLabel = primaryOrder ? ConfigDictionary?.caseUnitLabelPrimary : ConfigDictionary?.caseUnitLabel;
  otherUnitLabel = primaryOrder ? ConfigDictionary?.otherUnitLabelPrimary : ConfigDictionary?.otherUnitLabel;
  packUnitLabel = primaryOrder ? ConfigDictionary?.packUnitLabelPrimary : ConfigDictionary?.packUnitLabel;
  currency = ConfigDictionary?.currency;

  return {
    SKUCODE: "SKU Code",
    PROMOTION: "Promotion",
    PIECEPRICE: `Price (Each ${packUnitLabel})`,
    AMOUNT: "Amount",
    DISCOUNT: "Discount",
    FINALAMOUNT: "Final Amount",
    PIECEQUANTITY: `QTY (${packUnitLabel})`,
    CASEQUANTITY: `QTY (${caseUnitLabel})`,
    OTHERQUANTITY: `QTY (${otherUnitLabel})`,
    SKUNAME: "SKU Name",
    CASEPRICE: `Price (Each ${caseUnitLabel})`,
    CATEGORY: "Category",
  };
};

export const checkIfStockExceeded = (
  caseQty: number,
  otherQty: number,
  pieceQty: number,
  caseStock: number,
  otherStock: number,
  pieceStock: number,
  caseToPieceQty: number,
  otherToPieceQty: number,
  type?: UOM
) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");

  if (ConfigDictionary.uomConversion) {
    const normalizedQty = caseQty * caseToPieceQty + otherQty * otherToPieceQty + pieceQty;
    const normalizedStock = caseStock * caseToPieceQty + otherStock * otherToPieceQty + pieceStock;
    return normalizedQty > normalizedStock;
  }

  const qty = type === UOM.CASE ? caseQty : type === UOM.OTHER ? otherQty : pieceQty;
  const stock = type === UOM.CASE ? caseStock : type === UOM.OTHER ? otherStock : pieceStock;
  return qty > stock;
};

export const checkIfQtyBelowMOQ = (
  caseQty: number,
  otherQty: number,
  pieceQty: number,
  caseToPieceQty: number,
  otherToPieceQty: number,
  minimumOrderQuantity: number,
  type?: UOM
) => {
  const normalizedQty = caseQty * caseToPieceQty + otherQty * otherToPieceQty + pieceQty;
  return normalizedQty < minimumOrderQuantity;
};

export const getEditOrderConfig = (
  handleQuantityChange: (paramRow: any, changedQuantity: any, type: UOM) => void,
  handleOnBlur: (paramRow: any) => void,
  handleRemove: (paramRow: any) => void,
  handlePromotionModalVisibility: (paramRow: viewManageOrderRowsData) => void,
  insideManageInvoice: boolean,
  ViewOrderEditTableHeader: Record<string, string>,
  disableStockCheck: boolean = false,
  primaryOms: boolean = false,
  enableMOQCheck: boolean = false,
  showCompanyStockOMS: boolean = false,
) => {
  return {
    columns: [
      {
        headerName: "skuNameAndCode",
        mappedValue: "batchCode",
        width: primaryOms ? 200 : 220,
        renderHeader: (value: any) => (
          <Box display="flex" sx={{ flexDirection: "column", alignItems: "left" }}>
                    <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
                      <span>{"SKU Name"}</span>
                      <span className="customTableInfo_marginTopNegative">{"& Code"}</span>
                    </Box>
                  </Box>
        ),
        renderCell: (value: any, row: any) => {
          return (
            <Tooltip title={`${row?.skuDescription} - ${value}`}>
              <Box className="sku_name_truncate_text">
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ ...TruncatedTextUptoLine(1), fontWeight: 500, fontSize: "13px" }}>
                    {row?.skuDescription}
                  </span>
                  <span style={{ ...TruncatedTextUptoLine(1), fontSize: "12px", color: "#666" }}>
                    {value}
                  </span>
                </div>
              </Box>
            </Tooltip>
          );
        },
      },
       ...(( primaryOms && showCompanyStockOMS )
      ? [
          {
            headerName: "Company Stock",
            mappedValue: "availableStock",
            width: 130,
            renderHeader: (value: any) => (
              <Box display={"flex"} sx={{ flexDirection: "column", alignItems: "center" }}>
                <Box display={"flex"} flexDirection={"column"} sx={{ textAlign: "left" }}>
                  <span>{"Company Stock"}</span>
                  <span>{`(${caseUnitLabel})`}</span>
                </Box>
              </Box>
            ),
            renderCell: (value: any, row: any) => {
              const {
                availableStock,
                initialCaseQuantity
              } = row;
              
              const orderStatusPending = row?.status === "PENDING";

              return (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <MultiQtyCell
                  pcQty={0}
                  csQty={value?.cs + (orderStatusPending ? 0 : initialCaseQuantity)}
                  otherQty={0}
                  viewprimaryorderconfigs={true}
                  singleQtyUom={caseUnitLabel}
                />
              </div>
              );
            },
          },
        ]
      : []),
// Conditionally include the Promotion column based on primaryOms
...(!primaryOms
  ? [
      {
        headerName: ViewOrderEditTableHeader?.PROMOTION,
        mappedValue: "promotion",
        width: 115,
        renderCell: (value: any, row: any) => {
          if (!Array.isArray(row?.discountInfo) || row.discountInfo.length === 0) return <div>-</div>;
          return (
            <div
              className="view-discounts-container"
              onClick={() => handlePromotionModalVisibility(row)}
            >
              <span>View Promos</span>
              <CircledRight style={{ width: "10px" }} />
            </div>
          );
        },
      },
    ]
  : []),
      {
        headerName: ViewOrderEditTableHeader?.CASEQUANTITY,
        mappedValue: "caseQuantity",
        width: 70,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignCenter">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Qty"}</span>
              <div className="customTableInfo_marginTopNegative">
                <span className="customTableInfo_footerOrderLightFont">{`(${caseUnitLabel})`}</span>
                <BorderColorIcon style={{ color: "#17B4A3", fontSize: "13px" }} />
              </div>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {
          const showStockExceedWarning =
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && !disableStockCheck;
          const showMOQWarning = 
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
          let quantityExceed = false;
          const { finalCaseStock, finalOtherStock, finalPieceStock} = calculateFinalStock(row);
          quantityExceed =
            checkIfStockExceeded(
              row?.caseQuantity,
              row?.otherUnitQuantity,
              row?.pieceQuantity,
              finalCaseStock,
              finalOtherStock,
              finalPieceStock,
              row?.caseToPieceQty,
              row?.otherToPieceQty,
              UOM.CASE
            ) && showStockExceedWarning;
          const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
          const {uomConversion} = ConfigDictionary
          const disableCaseUom = (!row?.uom?.includes("all") && !row?.uom?.includes("cs")) || (uomConversion && row?.caseToPieceQty === 0);

          let quantityBelowMOQ = false
          quantityBelowMOQ = checkIfQtyBelowMOQ(
            row?.caseQuantity,
            row?.otherUnitQuantity,
            row?.pieceQuantity,
            row?.caseToPieceQty,
            row?.otherToPieceQty,
            row?.minimumOrderQuantity,
          ) && showMOQWarning;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {(quantityExceed || quantityBelowMOQ) && (!disableCaseUom) && (
                <DisclaimerIcon
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "5px",
                    zIndex: 1,
                  }}
                />
              )}
              <input
                style={{
                  borderColor: (quantityExceed || quantityBelowMOQ) && (!disableCaseUom) ? "#F06C5B" : "",
                  background: disableCaseUom ? "#f5f5f5" : (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                }}
                disabled={disableCaseUom}
                value={value}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (/^\d{0,4}$/.test(inputValue) && !isNaN(+inputValue)) {
                    handleQuantityChange(row, +inputValue, UOM.CASE);
                  }
                }}
                onBlur={() => handleOnBlur(row)}
                className="view_order_case_quantity_input"
              />
            </Box>
          );
        },
      },
      // ...(
      //   primaryOms ? [{
      //     headerName: ViewOrderEditTableHeader?.CASEPRICE,
      //     mappedValue: "casePrice",
      //     width: 90,
      //     textAlign: "left",
      //     renderHeader: (value: any) => (
      //       <Box
      //         display={"flex"}
      //         flexDirection={"column"}
      //         sx={{ textAlign: "left" }}
      //       >
      //         <span>{"Price"}</span>
      //         <span>{`(Each ${caseUnitLabel})`}</span>
      //       </Box>
      //     ),
      //     currencySymbol: true,
      //   }]: []
      // ),
      ...(otherUnitLabel
        ? [
            {
              headerName: ViewOrderEditTableHeader?.OTHERQUANTITY,
              mappedValue: "otherUnitQuantity",
              width: 70,
              textAlign: "left",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignCenter">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Qty"}</span>
                    <div className="customTableInfo_marginTopNegative">
                      <span className="customTableInfo_footerOrderLightFont">{`(${otherUnitLabel?.slice(0, 3)})`}</span>
                      <BorderColorIcon style={{ color: "#17B4A3", fontSize: "13px" }} />
                    </div>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const showStockExceedWarning = (ordersWithStockExceedingWarning?.includes(
                  row?.status
                ) || insideManageInvoice) && !disableStockCheck;
                const showMOQWarning = 
                  (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
                let quantityExceed = false;
                const { finalCaseStock, finalOtherStock, finalPieceStock} = calculateFinalStock(row);
                quantityExceed =
                  checkIfStockExceeded(
                    row?.caseQuantity,
                    row?.otherUnitQuantity,
                    row?.pieceQuantity,
                    finalCaseStock,
                    finalOtherStock,
                    finalPieceStock,
                    row?.caseToPieceQty,
                    row?.otherToPieceQty,
                    UOM.OTHER
                  ) && showStockExceedWarning
                let quantityBelowMOQ = false
                quantityBelowMOQ = checkIfQtyBelowMOQ(
                  row?.caseQuantity,
                  row?.otherUnitQuantity,
                  row?.pieceQuantity,
                  row?.caseToPieceQty,
                  row?.otherToPieceQty,
                  row?.minimumOrderQuantity,
                ) && showMOQWarning;
                const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
                const {uomConversion, disableOtherUnitNameWiseCavinKair} = ConfigDictionary
                const disableOtherUom = (!row?.uom?.includes("all") && !row?.uom?.includes("other")) || (uomConversion && row?.otherToPieceQty === 0) || (disableOtherUnitNameWiseCavinKair && row?.otherUnitName?.trim().length === 0);

                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {(quantityExceed || quantityBelowMOQ) && (!disableOtherUom) && (
                      <DisclaimerIcon
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "5px",
                          zIndex: 1,
                        }}
                      />
                    )}
                    <input
                      style={{
                        borderColor: (quantityExceed || quantityBelowMOQ) && (!disableOtherUom) ? "#F06C5B" : "",
                        background: disableOtherUom ? "#f5f5f5" : (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                      }}
                      value={value}
                      disabled={disableOtherUom}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (/^\d{0,4}$/.test(inputValue) && !isNaN(+inputValue)) {
                          handleQuantityChange(row, +inputValue, UOM.OTHER);
                        }
                      }}
                      onBlur={() => handleOnBlur(row)}
                      className="view_order_case_quantity_input"
                    />
                  </Box>
                );
              },
            },
          ]
        : []),
        ...(!primaryOms
          ? [ {
        headerName: ViewOrderEditTableHeader?.PIECEQUANTITY,
        mappedValue: "pieceQuantity",
        width: 70,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignCenter">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Qty"}</span>
              <div className="customTableInfo_marginTopNegative">
                <span className="customTableInfo_footerOrderLightFont">{`(${packUnitLabel})`}</span>
                <BorderColorIcon style={{ color: "#17B4A3", fontSize: "13px" }} />
              </div>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {
          const showStockExceedWarning =
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && !disableStockCheck;
          const showMOQWarning = 
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
          let quantityExceed = false;
          const { finalCaseStock, finalOtherStock, finalPieceStock} = calculateFinalStock(row);
              
          quantityExceed =
            checkIfStockExceeded(
              row?.caseQuantity,
              row?.otherUnitQuantity,
              row?.pieceQuantity,
              finalCaseStock,
              finalOtherStock,
              finalPieceStock,
              row?.caseToPieceQty,
              row?.otherToPieceQty,
              UOM.PIECE
            ) && showStockExceedWarning;
          let quantityBelowMOQ = false
            quantityBelowMOQ = checkIfQtyBelowMOQ(
              row?.caseQuantity,
              row?.otherUnitQuantity,
              row?.pieceQuantity,
              row?.caseToPieceQty,
              row?.otherToPieceQty,
              row?.minimumOrderQuantity,
            ) && showMOQWarning;

          const disablePieceUom = !row?.uom?.includes("all") && !row?.uom?.includes("ea");

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {(quantityExceed || quantityBelowMOQ) && (!disablePieceUom) && (
                <DisclaimerIcon
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "5px",
                    zIndex: 1,
                  }}
                />
              )}
              <input
                style={{
                  borderColor: ((quantityExceed || quantityBelowMOQ)) && (!disablePieceUom)? "#F06C5B" : "",
                  background: disablePieceUom ? "#f5f5f5" : (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                }}
                value={value}
                disabled={disablePieceUom}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (/^\d{0,4}$/.test(inputValue) && !isNaN(+inputValue)) {
                    handleQuantityChange(row, +inputValue, UOM.PIECE);
                  }
                }}
                onBlur={() => handleOnBlur(row)}
                className="view_order_pack_quantity_input"
              />
            </Box>
          );
        },
      }] : []),
      ...(disableStockCheck ? [] : [
        {
          headerName: "",
          mappedValue: "availableStock",
          width: enableMOQCheck ? 250 : 450,
          renderHeader: (value: any) => <Box>Available Stock</Box>,
          renderCell: (value: any, row: any) => {
            const {
              caseQuantity,
              pieceQuantity,
              otherUnitQuantity,
              availableStock,
              initialPieceQuantity,
              initialCaseQuantity,
              initialOtherUnitQuantity,
              caseToPieceQty,
              otherToPieceQty,
              otherUnitName
            } = row;
  
            const orderStatusPending = row?.status === "PENDING";
            let isExceedQty = false;
            const finalCaseStock =
              availableStock?.cs + (orderStatusPending ? 0 : initialCaseQuantity);
            const finalPieceStock =
              availableStock?.pc + (orderStatusPending ? 0 : initialPieceQuantity);
            const finalOtherStock =
              availableStock?.other + (orderStatusPending ? 0 : initialOtherUnitQuantity);
  
            const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
            if (ConfigDictionary.uomConversion) {
              isExceedQty = checkIfStockExceeded(
                caseQuantity,
                otherUnitQuantity,
                pieceQuantity,
                finalCaseStock,
                finalOtherStock,
                finalPieceStock,
                caseToPieceQty,
                otherToPieceQty
              );
            } else {
              isExceedQty =
                caseQuantity > finalCaseStock ||
                pieceQuantity > finalPieceStock ||
                otherUnitQuantity > finalOtherStock;
            }
  
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <MultiQtyCell
                  pcQty={value?.pc + (orderStatusPending ? 0 : initialPieceQuantity)}
                  csQty={value?.cs + (orderStatusPending ? 0 : initialCaseQuantity)}
                  otherQty={value?.other + (orderStatusPending ? 0 : initialOtherUnitQuantity)}
                  otherUnitName={otherUnitName}
                />
                {isExceedQty && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#FFF4F4",
                      borderRadius: "10px",
                      width: "200px",
                      justifyContent: "center",
                      gap: "4px",
                      padding: "6px 6px 6px 10px",
                    }}
                  >
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "5px"
                      }}
                    >
                      <EncircledDisclaimerIcon
                        style={{ height: "10px", width: "10px", marginTop: "-10px" }}
                      />
                      {isExceedQty && (<span
                        style={{
                          color: "#F35E5F",
                          fontSize: "9px",
                          width: "95%",
                          lineHeight: "1.3",
                          marginLeft: "2px",
                        }}
                      >
                        {enableMOQCheck ? "Ordered quantity exceeds available stock." : "You are attempting to enter a quantity that exceeds the available stock."}
                      </span>)
                      }
                    </p>
                  </div>
                )}
              </div>
            );
          },
        }
      ]),
      ...(!enableMOQCheck ? [] : [
        {
          headerName: "",
          mappedValue: "minimumOrderQuantity",
          width: !disableStockCheck ? 250 : 450,
          renderHeader: (value: any) => <Box>Minimum Billable Quantity</Box>,
          renderCell: (value: any, row: any) => {
            const {
              caseQuantity,
              pieceQuantity,
              otherUnitQuantity,
              caseToPieceQty,
              otherToPieceQty,
              minimumOrderQuantity,
              otherUnitName,
              uom
            } = row;
  
            const orderStatusPending = row?.status === "PENDING";
            let quantityBelowMOQ = false
            quantityBelowMOQ = checkIfQtyBelowMOQ(
              caseQuantity,
              otherUnitQuantity,
              pieceQuantity,
              caseToPieceQty,
              otherToPieceQty,
              minimumOrderQuantity,
              uom,
            )

            const { convertedCaseQty, convertedOtherQty, convertedPieceQty } = performUomConversion(0, 0, minimumOrderQuantity, uom, caseToPieceQty, otherToPieceQty, 0, 0, 0, true, otherUnitName)
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <MultiQtyCell
                  pcQty={convertedPieceQty}
                  csQty={convertedCaseQty}
                  otherQty={convertedOtherQty}
                  otherUnitName={otherUnitName}
                />
                {quantityBelowMOQ && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#FFF4F4",
                      borderRadius: "9px",
                      width: "200px",
                      justifyContent: "center",
                      gap: "4px",
                      padding: "6px 6px 6px 10px",
                    }}
                  >
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "5px",
                      }}
                    >
                      <EncircledDisclaimerIcon
                        style={{ height: "10px", width: "10px", marginTop: "-10px" }}
                      />
                      <span
                        style={{
                          color: "#F35E5F",
                          fontSize: "9px",
                          width: "95%",
                          lineHeight: "1.3",
                          marginLeft: "2px",
                        }}
                      >
                       Ordered quantity below Minimum Billable Quantity.
                      </span>
                    </p>
                  </div>
                )}
              </div>
            );
          },
        }
      ])
      ,
      {
        headerName: "",
        mappedValue: "",
        width: 35,
        renderCell: (value: any, row: any) => {
          return (
            <button
              className="view_order_manage_order_table_button"
              onClick={() => {
                handleRemove(row);
              }}
            >
              <SubtractIcon style={{ width: "16px", height: "16px" }} />
            </button>
          );
        },
      },
    ],
  };
};

export const getViewOrderConfig = (
  handlePromotionModalVisibility: (paramRow: viewManageOrderRowsData) => void,
  insideManageInvoice: boolean,
  ViewOrderEditTableHeader: Record<string, string>,
  disableStockCheck: boolean = false,
  hideItemLevelTaxInfo: boolean = false,
  primaryOms: boolean = false,
  enableMOQCheck: boolean = false,
) => {

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {showTotalConvertedCasesPrimaryOMS,  otherUnitLabelPrimary, batchIdConfiguration = false} = ConfigDictionary

  return {
    columns: [
      {
        headerName: "skuNameAndCode",
        mappedValue: "batchCode",
        width: 220,
        renderHeader: (value: any) => (
          <Box display="flex" sx={{ flexDirection: "column", alignItems: "left" }}>
                    <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
                      <span>{"SKU Name"}</span>
                      <span className="customTableInfo_marginTopNegative">{"& Code"}</span>
                    </Box>
                  </Box>
        ),
        renderCell: (value: any, row: any) => {
          return (
            <Tooltip title={`${row?.skuDescription} - ${value}`}>
              <Box className="sku_name_truncate_text ViewOrderConfig_skuCode">
                {row?.freeItem && (
                  <span className="ViewOrderConfig_freeText">FREE</span>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ ...TruncatedTextUptoLine(1), fontWeight: 500, fontSize: "13px" }}>
                    {row?.skuDescription}
                  </span>
                  <span style={{ ...TruncatedTextUptoLine(1), fontSize: "12px", color: "#666" }}>
                    {value}
                  </span>
                </div>
              </Box>
            </Tooltip>
          );
        },
      },
      ...((insideManageInvoice && batchIdConfiguration)) ? [
        {
          headerName: "Batch Id",
          mappedValue: "batchId",
          width: 100,
          renderHeader: (value: any) => (
            <Box display={"flex"} flexDirection={"column"}>
              <span>{"Batch ID"}</span>
            </Box>
          ),
          renderCell: (value: any, row: any) => {
              return (
                  <Tooltip title={value}>
                  <Box className="sku_name_truncate_text">
                    <span style={TruncatedTextUptoLine(2)}>{value ?? "-"}</span>
                  </Box>
                </Tooltip>
              );
            },
        }
      ] : [],
     // Conditionally include the Promotion column based on primaryOms
     ...(!primaryOms
      ? [
          {
            headerName: ViewOrderEditTableHeader?.PROMOTION,
            mappedValue: "promotion",
            width: 115,
            renderCell: (value: any, row: any) => {
              if (!Array.isArray(row?.discountInfo) || row.discountInfo.length === 0) return <div>-</div>;
              return (
                <div
                  className="view-discounts-container"
                  onClick={() => handlePromotionModalVisibility(row)}
                >
                  <span>View Promos</span>
                  <CircledRight style={{ width: "10px" }} />
                </div>
              );
            },
          },
        ]
      : []),

      ...(( otherUnitLabelPrimary && primaryOms)
        ? [
            {
              headerName: ViewOrderEditTableHeader?.OTHERQUANTITY,
              mappedValue: "otherUnitQuantity",
              width: 60,
              textAlign: "center",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignCenter">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Qty"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${otherUnitLabel?.slice(0, 3)})`}</span>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const showStockExceedWarning = (ordersWithStockExceedingWarning?.includes(
                  row?.status
                ) || insideManageInvoice) && !disableStockCheck;
                const showMOQWarning = 
                  (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
                const quantityExceed =
                  row?.status === "PENDING"
                    ? checkIfStockExceeded(
                        row?.caseQuantity,
                        row?.otherUnitQuantity,
                        row?.pieceQuantity,
                        row?.availableStock?.cs,
                        row?.availableStock?.other,
                        row?.availableStock?.pc,
                        row?.caseToPieceQty,
                        row?.otherToPieceQty,
                        UOM.OTHER
                      ) && showStockExceedWarning
                    : false;
                let quantityBelowMOQ = false
                quantityBelowMOQ = checkIfQtyBelowMOQ(
                  row?.caseQuantity,
                  row?.otherUnitQuantity,
                  row?.pieceQuantity,
                  row?.caseToPieceQty,
                  row?.otherToPieceQty,
                  row?.minimumOrderQuantity,
                ) && showMOQWarning;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "centet",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {(quantityExceed || quantityBelowMOQ) && (
                      <DisclaimerIcon
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-5px",
                          zIndex: 1,
                        }}
                      />
                    )}
                    <div
                      className="view_order_pack_quantity_input style_adjust"
                      style={{
                        borderColor: (quantityExceed || quantityBelowMOQ) ? "#F06C5B" : "",
                        background: (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                      }}
                    >
                      {value}
                    </div>
                  </Box>
                );
              },
            },
          ]
        : []),

      ...(caseUnitLabel ? [
        {
          headerName: ViewOrderEditTableHeader?.CASEQUANTITY,
          mappedValue: "caseQuantity",
          width: 60,
          textAlign: "center",
          renderHeader: (value: any) => (
            <div className="customTableInfo_flexColumnAlignCenter">
              <div className="customTableInfo_flexColumnTextLeft">
                <span>{"Qty"}</span>
                <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${caseUnitLabel})`}</span>
              </div>
            </div>
        ),
        renderCell: (value: any, row: any) => {
          const showStockExceedWarning =
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && !disableStockCheck;
          const showMOQWarning = 
            (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
          
          const quantityExceed =
            row?.status === "PENDING"
              ? checkIfStockExceeded(
                  row?.caseQuantity,
                  row?.otherUnitQuantity,
                  row?.pieceQuantity,
                  row?.availableStock?.cs,
                  row?.availableStock?.other,
                  row?.availableStock?.pc,
                  row?.caseToPieceQty,
                  row?.otherToPieceQty,
                  UOM.CASE
                ) && showStockExceedWarning
              : false;
          let quantityBelowMOQ = false
          quantityBelowMOQ = checkIfQtyBelowMOQ(
            row?.caseQuantity,
            row?.otherUnitQuantity,
            row?.pieceQuantity,
            row?.caseToPieceQty,
            row?.otherToPieceQty,
            row?.minimumOrderQuantity,
          ) && showMOQWarning;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "centet",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {(quantityExceed || quantityBelowMOQ) && (
                <DisclaimerIcon
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-5px",
                    zIndex: 1,
                  }}
                />
              )}
              <div
                className="view_order_pack_quantity_input style_adjust"
                style={{
                  borderColor: (quantityExceed || quantityBelowMOQ) ? "#F06C5B" : "",
                  background: (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                }}
              >
                  {value}
                </div>
              </Box>
            );
          },
        },
      ] : []),
      ...( (otherUnitLabel && !primaryOms)
        ? [
            {
              headerName: ViewOrderEditTableHeader?.OTHERQUANTITY,
              mappedValue: "otherUnitQuantity",
              width: 60,
              textAlign: "center",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignCenter">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Qty"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${otherUnitLabel?.slice(0, 3)})`}</span>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const showStockExceedWarning = (ordersWithStockExceedingWarning?.includes(
                  row?.status
                ) || insideManageInvoice) && !disableStockCheck;
                const showMOQWarning = 
                  (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
                const quantityExceed =
                  row?.status === "PENDING"
                    ? checkIfStockExceeded(
                        row?.caseQuantity,
                        row?.otherUnitQuantity,
                        row?.pieceQuantity,
                        row?.availableStock?.cs,
                        row?.availableStock?.other,
                        row?.availableStock?.pc,
                        row?.caseToPieceQty,
                        row?.otherToPieceQty,
                        UOM.OTHER
                      ) && showStockExceedWarning
                    : false;
                let quantityBelowMOQ = false
                quantityBelowMOQ = checkIfQtyBelowMOQ(
                  row?.caseQuantity,
                  row?.otherUnitQuantity,
                  row?.pieceQuantity,
                  row?.caseToPieceQty,
                  row?.otherToPieceQty,
                  row?.minimumOrderQuantity,
                ) && showMOQWarning;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "centet",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {(quantityExceed || quantityBelowMOQ) && (
                      <DisclaimerIcon
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-5px",
                          zIndex: 1,
                        }}
                      />
                    )}
                    <div
                      className="view_order_pack_quantity_input style_adjust"
                      style={{
                        borderColor: (quantityExceed || quantityBelowMOQ) ? "#F06C5B" : "",
                        background: (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                      }}
                    >
                      {value}
                    </div>
                  </Box>
                );
              },
            },
          ]
        : []),

        ...((!primaryOms && packUnitLabel)
        ? [
          {
            headerName: ViewOrderEditTableHeader?.PIECEQUANTITY,
            mappedValue: "pieceQuantity",
            width: 60,
            textAlign: "center",
            renderHeader: (value: any) => (
              <div className="customTableInfo_flexColumnAlignCenter">
                <div className="customTableInfo_flexColumnTextLeft">
                  <span>{"Qty"}</span>
                  <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${packUnitLabel})`}</span>
                </div>
              </div>
            ),
            renderCell: (value: any, row: any) => {
              const showStockExceedWarning =
                (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && !disableStockCheck;
              const showMOQWarning = 
                (ordersWithStockExceedingWarning?.includes(row?.status) || insideManageInvoice) && enableMOQCheck;
              const quantityExceed =
                row?.status === "PENDING"
                  ? checkIfStockExceeded(
                      row?.caseQuantity,
                      row?.otherUnitQuantity,
                      row?.pieceQuantity,
                      row?.availableStock?.cs,
                      row?.availableStock?.other,
                      row?.availableStock?.pc,
                      row?.caseToPieceQty,
                      row?.otherToPieceQty,
                      UOM.PIECE
                    ) && showStockExceedWarning
                  : false;
              let quantityBelowMOQ = false
              quantityBelowMOQ = checkIfQtyBelowMOQ(
                row?.caseQuantity,
                row?.otherUnitQuantity,
                row?.pieceQuantity,
                row?.caseToPieceQty,
                row?.otherToPieceQty,
                row?.minimumOrderQuantity,
              ) && showMOQWarning;
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "centet",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {(quantityExceed || quantityBelowMOQ) && (
                    <DisclaimerIcon
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: "-5px",
                        zIndex: 1,
                      }}
                    />
                  )}
                  <div
                    className="view_order_pack_quantity_input style_adjust"
                    style={{
                      borderColor: (quantityExceed || quantityBelowMOQ) ? "#F06C5B" : "",
                      background: (quantityExceed || quantityBelowMOQ) ? "#FFF6F5" : "",
                    }}
                  >
                    {value}
                  </div>
                </Box>
              );
            },
          }
        ] : []),


        ...( (primaryOms && showTotalConvertedCasesPrimaryOMS)
          ? [
              {
                headerName: "totalConvertedCasesPrimaryOMS",
                mappedValue: "totalConvertedCasesPrimaryOMS",
                width: 80,
                textAlign: "center",
                renderHeader: () => (
                  <div className="customTableInfo_flexColumnAlignCenter">
                    <div className="customTableInfo_flexColumnTextCenter">
                      <span>{"Total Qty"}</span>
                      <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{"(CS)"}</span>
                    </div>
                  </div>
                ),
                renderCell: (value: any) => (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="view_order_pack_quantity_input style_adjust">{value}</div>
                  </Box>
                ),
              },
            ]
          : []),



      ...((!primaryOms && packUnitLabel) ? [{
        headerName: ViewOrderEditTableHeader?.PIECEPRICE,
        mappedValue: "price",
        width: 80,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnTextLeft">
            <span>{"Price"}</span>
            <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${packUnitLabel})`}</span>
          </div>
        ),
        currencySymbol: true,
      }] : [{
        headerName: ViewOrderEditTableHeader?.CASEPRICE,
        mappedValue: "casePrice",
        width: 90,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnTextLeft">
            <span>{"Price"}</span>
            <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${caseUnitLabel})`}</span>
          </div>
        ),
        currencySymbol: true,
      }]),
      
      {
        headerName: ViewOrderEditTableHeader?.AMOUNT,
        mappedValue: "actualAmount",
        width: 115,
        textAlign: "left",
        currencySymbol: true,
      },
      {
        headerName: ViewOrderEditTableHeader?.DISCOUNT,
        mappedValue: "skuDiscountInfo",
        width: 110,
        textAlign: "left",
        renderCell: (value: any, row: any) => {

          const aggregateDiscountAmount = value?.reduce(
            (acc: any, curr: any) => {
              acc += Number(curr?.discountAmount);
              return acc;
            },
            0
          );

          const consolidatedDiscountObj = {
            discountType: "All",
            discountAmount: aggregateDiscountAmount,
          }

          return (
            <FormControl className="manage_order_filter">
            <Select
              labelId="dropdown_label"
              size="small"
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  padding: "0px !important",
                },
                width: "100%",
                fontSize: "14px",
                padding: "0px",
                "&.Mui-focused": {
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-icon": {
                  display: value?.length === 0 ? "none" : "block",
                  marginRight: "-11px",
                  color: "#b0b0b0"
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    ml: -1,
                    padding: 0,
                    boxShadow: "none",
                    border: "1px solid #DEDEDE",
                    borderRadius: "8px",
                  },
                },
                MenuListProps: {
                  sx: {
                    padding: "7px 0",
                  },
                },
              }}
              renderValue={() => {
                return (
                  <span style={{ color: "#7c7c7c", fontSize: "13px" }}>
                    <span>
                      {`${
                        currency ?? ""
                      }\u200A${truncateToTwoDecimals(consolidatedDiscountObj?.discountAmount ?? 0)}`}
                    </span>
                  </span>
                );
              }}
            >
              {value?.length > 0 && value?.map((item: any, index: any, arr: any) => (
                <MenuItem
                  key={item?.discountType}
                  value={item?.discountType}
                  className="menuItem-test-debug"
                  sx={{
                    padding: "2px 0px",
                    borderBottom:
                      index !== arr.length - 1
                        ? "1.5px dashed #EBEBEB"
                        : "none",
                    margin: "0 10px",
                    pointerEvents: "none"
                  }}
                >
                  <div className="loadout_dropdown_menu_item">
                  <span>
                {`${item?.discountType ?? ""}: ${currency ?? ""}${truncateToTwoDecimals(item?.discountAmount ?? 0)}`}
              </span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          
          );
        },
      },
      ...(hideItemLevelTaxInfo ? [] : [{
        headerName: "Tax Amount & (%)",
        mappedValue: "taxInfo",
        width: 150,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignStart">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Tax Applicable"}</span>
              <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{"Amount & (%)"}</span>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {

          const aggregateTaxAmount = value?.reduce((acc: any, curr: any) => {
            acc += Number(curr?.taxAmount);
            return acc;
          }, 0);
          const aggregateTaxPercent = value?.reduce((acc: any, curr: any) => {
            acc += Number(curr?.taxPercent);
            return acc;
          }, 0);

          const consolidatedTaxObj = {
            taxType: "All",
            taxAmount: aggregateTaxAmount,
            taxPercent: aggregateTaxPercent,
          };

          return (
            <FormControl className="manage_order_filter">
            <Select
              labelId="dropdown_label"
              size="small"
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  padding: "0px !important",
                },
                width: "100%",
                fontSize: "14px",
                padding: "0px",
                "&.Mui-focused": {
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                
                "& .MuiSelect-icon": {
                  display: value?.length === 0 ? "none" : "block",
                  marginRight: "-11px",
                  color: "#b0b0b0"
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    padding: 0,
                    boxShadow: "none",
                    border: "1px solid #DEDEDE",
                    borderRadius: "8px",
                  },
                },
                MenuListProps: {
                  sx: {
                    padding: "7px 0",
                  },
                },
              }}
              renderValue={() => {
                return (
                  <span style={{ color: "#7c7c7c", fontSize: "13px" }}>
                   <span>
                {`${
                  currency ?? ""
                }\u200A${truncateToTwoDecimals(consolidatedTaxObj?.taxAmount ?? 0)}`}
                {`(${consolidatedTaxObj?.taxPercent ?? 0}%)`}
              </span>
                  </span>
                );
              }}
            >
              
              {value?.length > 0 && value?.map((item: any, index: any, arr: any) => (
                <MenuItem
                  key={item?.taxType}
                  value={item?.taxType}
                  className="menuItem-test-debug"
                  sx={{
                    padding: "2px 0px",
                    borderBottom:
                      index !== arr.length - 1
                        ? "1.5px dashed #EBEBEB"
                        : "none",
                    margin: "0 10px",
                    pointerEvents: "none"
                  }}
                >
                  <div className="loadout_dropdown_menu_item">
                  <span>
                {`${item?.taxType ?? ""}: ${currency ?? ""}${truncateToTwoDecimals(item?.taxAmount ?? 0)}`}
                {`(${item?.taxPercent ?? 0}%)`}
              </span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          );
        },
      }]),
      {
        headerName: ViewOrderEditTableHeader?.FINALAMOUNT,
        textAlign: "left",
        renderHeader: (value: any) => (
          <Box display={"flex"} sx={{ flexDirection: "column", alignItems: "left" }}>
            <Box display={"flex"} flexDirection={"column"} sx={{ textAlign: "left" }}>
              <span>{"Final"}</span>
              <span className="customTableInfo_marginTopNegative">{"Amount"}</span>
            </Box>
          </Box>
        ),
        mappedValue: "finalAmount",
        width: 100,
        renderCell: (value: any) => (
          <Box>
            <span
              style={{
                textAlign: "left",
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {`${currency}\u200A${truncateToTwoDecimals(value) ?? ""}`}
            </span>
          </Box>
        ),
      },
    ],
  };
};


export const getMetaData1 = (excludeKeys: string[] = [], couponLabel?: string, insideManageInvoice?: boolean, viewQpsFocInSummary?: boolean): any[] => {
  // Build the label for totalQuantities based on truthy labels
  const availableLabels = [];
  if (caseUnitLabel) availableLabels.push(caseUnitLabel);
  if (otherUnitLabel) availableLabels.push(otherUnitLabel);
  if (packUnitLabel) availableLabels.push(packUnitLabel);

  const totalQuantitiesMeta = availableLabels.length > 0
    ? [{
        keys: "totalQuantities",
        label: `Qty (${availableLabels.join(", ")})`,
        index: 2,
        valueType: "Array",
        render: "Jsx",
      }]
    : [];

  const totalQuantitiesMetaFOC_QPS = (availableLabels.length > 0 && insideManageInvoice && viewQpsFocInSummary)
    ? [{
        keys: "totalQuantitiesFOC_QPS",
        label: `QPS FOC Qty`,
        index: 3,
        valueType: "Array",
        render: "Jsx",
      }]
    : [];

  const metaData = [
    {
      keys: "lineCount",
      label: "Total Products",
      index: 1,
      valueType: "Number",
    },
    ...totalQuantitiesMeta,
    ...totalQuantitiesMetaFOC_QPS,
    {
      keys: "totalAmount",
      label: "Total Amount",
      index: 4,
      valueType: "Number",
      currencySymbol: currency,
    },
    {
      keys: "discountValue",
      label: "Scheme Discount",
      index: 5,
      valueType: "Number",
      currencySymbol: currency,
    },
    {
      keys: "couponValue",
      label: couponLabel || "Coupon Discount",
      index: 6,
      valueType: "Number",
      currencySymbol: currency,
    },
    {
      keys: "additionalDistributorDiscount",
      label: "Add. Distr. Discount",
      index: 7,
      valueType: "Number",
      currencySymbol: currency,
    },
    {
      keys: "taxAmount",
      label: "Tax Amount",
      index: 8,
      valueType: "Number",
      currencySymbol: currency,
    },
    {
      keys: "netAmount",
      label: "Final Amount",
      index: 9,
      valueType: "Number",
      currencySymbol: currency,
      focusField: true,
    },
  ];

  // Filter out metadata entries whose keys are in the excludeKeys array
  return metaData.filter((item) => !excludeKeys.includes(item.keys));
};

export const getMetaData1V2 = (excludeKeys: string[] = [], couponLabel?: string, insideManageInvoice?: boolean, viewQpsFocInSummary?: boolean): any[] => {
  // Build the label for totalQuantities based on truthy labels
  const availableLabels = [];
  if (caseUnitLabel) availableLabels.push(caseUnitLabel);
  if (otherUnitLabel) availableLabels.push(otherUnitLabel);
  if (packUnitLabel) availableLabels.push(packUnitLabel);

  const totalQuantitiesMeta = availableLabels.length > 0
    ? [{
        keys: "totalQuantities",
        label: `Qty ordered`,
        index: 2,
        valueType: "Array",
        render: "Jsx",
      }]
    : [];

  const totalQuantitiesMetaFOC_QPS = (availableLabels.length > 0 && insideManageInvoice && viewQpsFocInSummary)
    ? [{
        keys: "totalQuantitiesFOC_QPS",
        label: `QPS FOC Qty`,
        index: 3,
        valueType: "Array",
        render: "Jsx",
      }]
    : [];

  const metaData = [
    {
      keys: "lineCount",
      label: "Total Products",
      index: 1,
      valueType: "Number",
    },
    ...totalQuantitiesMeta,
    ...totalQuantitiesMetaFOC_QPS,
    {
      keys: "source",
      label: "Order Source",
      index: 4,
      valueType: "String",
      render: "Jsx",

    },
    {
      keys: "totalAmount",
      label: "Total Amount",
      index: 4,
      valueType: "Number",
      currencySymbol: currency,

    },
    {
      keys: "totalDiscount",
      label: "Discount",
      index: 5,
      valueType: "Number",
      currencySymbol: currency,
      customStyles: {
        color: "#1863C5"
      },
      render: "Jsx",

    },
    // {
    //   keys: "discountValue",
    //   label: "Scheme Discount",
    //   index: 5,
    //   valueType: "Number",
    //   currencySymbol: currency,
    // },
    // {
    //   keys: "couponValue",
    //   label: couponLabel || "Coupon Discount",
    //   index: 6,
    //   valueType: "Number",
    //   currencySymbol: currency,
    // },
    // {
    //   keys: "additionalDistributorDiscount",
    //   label: "Add. Distr. Discount",
    //   index: 7,
    //   valueType: "Number",
    //   currencySymbol: currency,
    // },
    {
      keys: "taxAmount",
      label: "Tax Applicable",
      index: 8,
      valueType: "Number",
      currencySymbol: currency,
      customStyles: {
        color: "#B96D16"
      }
    },
    {
      keys: "netAmount",
      label: "Final Amount",
      index: 9,
      valueType: "Number",
      currencySymbol: currency,
      focusField: true,
      customStyles: {
        color: "#1B7713"
      }
    },
  ];

  // Filter out metadata entries whose keys are in the excludeKeys array
  return metaData.filter((item) => !excludeKeys.includes(item.keys));
};

export const getViewOrderConfigV2 = (
  handleQuantityChange: (
    paramRow: any,
    changedQuantity: any,
    type: UOM
  ) => void,
  handleOnBlur: (paramRow: any) => void,
  handleRemove: (paramRow: any) => void,
  handlePromotionModalVisibility: (paramRow: viewManageOrderRowsData) => void,
  insideManageInvoice: boolean,
  ViewOrderEditTableHeader: Record<string, string>,
  disableStockCheck: boolean = false,
  hideItemLevelTaxInfo: boolean = false,
  primaryOms: boolean = false,
  enableMOQCheck: boolean = false,
  isEditing: boolean = false,
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
  updateQuantityAndBlur: (row: any, newValue: number, type: UOM) => void,
  handleEditClick: () => Promise<void>,
  canEditOrder: boolean,

  cokenpFlatTaxAmountField : boolean=false,
  isPartialOrder?: boolean,

  enableRemainingStocksLabel?: boolean,
  hideItemLevelDiscountInfo: boolean = false,
  columnsMetadataConfirguration?: ColumnsConfiguration
) => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const {
    showTotalConvertedCasesPrimaryOMS,
    otherUnitLabelPrimary,
    batchIdConfiguration = false,
  } = ConfigDictionary;

  let resultedColumns = {
    columns: [
      {
        headerName: "skuNameAndCode",
        mappedValue: "batchCode",
        width: columnsMetadataConfirguration?.["skuNameAndCode"]  ?? 130,
        renderHeader: (value: any) => (
          <Box
            display="flex"
            sx={{ flexDirection: "column", alignItems: "left" }}
          >
            <Box
              display="flex"
              flexDirection="column"
              sx={{ textAlign: "left" }}
            >
              <span>{"SKU Name"}</span>
              <span className="customTableInfo_marginTopNegative">
                {"& Code"}
              </span>
              
            </Box>
          </Box>
        ),
        renderCell: (value: any, row: any) => {
          return (
            <Tooltip title={`${row?.skuDescription} - ${value}`}>
              <Box className="sku_name_truncate_text ViewOrderConfig_skuCode">
                {row?.freeItem && (
                  <span className="ViewOrderConfig_freeText">FREE</span>
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <span
                    style={{
                      ...TruncatedTextUptoLine(1),
                      fontWeight: 500,
                      fontSize: "12px",
                    }}
                  >
                    {row?.skuDescription}
                  </span>
                  <span
                    style={{
                      ...TruncatedTextUptoLine(1),
                      fontSize: "11px",
                      color: "#666",
                    }}
                  >
                    {value}
                  </span>
                  {(Array.isArray(row?.discountInfo) &&
                  row.discountInfo.length !== 0) && 
                  (!row?.containsMultipleBatchId || !insideManageInvoice) &&
                  <div
                    className="view-discounts-container"
                    
                    onClick={() => handlePromotionModalVisibility(row)}
                  >
                    <span style={{
                      fontSize: "9px"
                    }}>View Promos</span>
                    <CircledRight style={{ width: "8px" }} />
                  </div>}
                </div>
              </Box>
            </Tooltip>
          );
        },
      },
      ...(insideManageInvoice && batchIdConfiguration
        ? [
            {
              headerName: "Batch Id",
              mappedValue: "batchId",
              width: columnsMetadataConfirguration?.["Batch Id"]  ?? 120,
              canOpen: true,
              renderHeader: (value: any) => (
                <Box display={"flex"} flexDirection={"column"}>
                  <span>{"Batch ID"}</span>
                </Box>
              ),
              renderCell: (value: any, row: any) => {
                if (row?.containsMultipleBatchId) return (
                   <div
                    className="view-discounts-container"
                    style={{userSelect: "none"}}
                  >
                    <span >View Batch IDs</span>
                    <CircledRight style={{ width: "10px" }} />
                  </div>
                )
                return (
                  <Tooltip title={value}>
                    <Box className="sku_name_truncate_text">
                      <span style={TruncatedTextUptoLine(2)}>
                        {value ?? "-"}
                      </span>
                    </Box>
                  </Tooltip>
                );
              },
            },
          ]
        : []),

      ...(otherUnitLabelPrimary && primaryOms
        ? [
            {
              headerName: ViewOrderEditTableHeader?.OTHERQUANTITY,
              mappedValue: "otherUnitQuantity",
              width: columnsMetadataConfirguration?.["otherUnitQuantity"]  ?? 60,
              textAlign: "center",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignCenter">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Qty"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${otherUnitLabel?.slice(
                      0,
                      3
                    )})`}</span>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const showStockExceedWarning =
                  (ordersWithStockExceedingWarning?.includes(row?.status) ||
                    insideManageInvoice) &&
                  !disableStockCheck;
                const showMOQWarning =
                  (ordersWithStockExceedingWarning?.includes(row?.status) ||
                    insideManageInvoice) &&
                  enableMOQCheck;
                const quantityExceed =
                  row?.status === "PENDING"
                    ? checkIfStockExceeded(
                        row?.caseQuantity,
                        row?.otherUnitQuantity,
                        row?.pieceQuantity,
                        row?.availableStock?.cs,
                        row?.availableStock?.other,
                        row?.availableStock?.pc,
                        row?.caseToPieceQty,
                        row?.otherToPieceQty,
                        UOM.OTHER
                      ) && showStockExceedWarning
                    : false;
                let quantityBelowMOQ = false;
                quantityBelowMOQ =
                  checkIfQtyBelowMOQ(
                    row?.caseQuantity,
                    row?.otherUnitQuantity,
                    row?.pieceQuantity,
                    row?.caseToPieceQty,
                    row?.otherToPieceQty,
                    row?.minimumOrderQuantity
                  ) && showMOQWarning;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "centet",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {(quantityExceed || quantityBelowMOQ) && (
                      <DisclaimerIcon
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-5px",
                          zIndex: 1,
                        }}
                      />
                    )}
                    <div
                      className="view_order_pack_quantity_input style_adjust"
                      style={{
                        borderColor:
                          quantityExceed || quantityBelowMOQ ? "#F06C5B" : "",
                        background:
                          quantityExceed || quantityBelowMOQ ? "#FFF6F5" : "",
                      }}
                    >
                      {value}
                    </div>
                  </Box>
                );
              },
            },
          ]
        : []),

      {
        headerName: "Ordered Quantity",
        width: columnsMetadataConfirguration?.["Ordered Quantity"]  ?? 200,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignStart">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Ordered Quantity"}</span>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {
          const showStockExceedWarning: boolean =
            (ordersWithStockExceedingWarning?.includes(row?.status) ||
              insideManageInvoice) &&
            !disableStockCheck;
          const showMOQWarning: boolean =
            (ordersWithStockExceedingWarning?.includes(row?.status) ||
              insideManageInvoice) &&
            enableMOQCheck;
          let quantityExceed: boolean = false;
          const ConfigDictionary = JSON.parse(
            localStorage.getItem("ConfigDictionary") ?? "{}"
          );
          const { finalCaseStock, finalOtherStock, finalPieceStock } =
            calculateFinalStock(row);

          if (ConfigDictionary.uomConversion) {
            quantityExceed = checkIfStockExceeded(
              row?.caseQuantity,
              row?.otherUnitQuantity,
              row?.pieceQuantity,
              finalCaseStock,
              finalOtherStock,
              finalPieceStock,
              row?.caseToPieceQty,
              row?.otherToPieceQty
            );
          } else {
            quantityExceed =
              row?.caseQuantity > finalCaseStock ||
              row?.pieceQuantity > finalPieceStock ||
              row?.otherUnitQuantity > finalOtherStock;
          }
          quantityExceed = quantityExceed && showStockExceedWarning;
          let quantityBelowMOQ: boolean = false;
          quantityBelowMOQ =
            checkIfQtyBelowMOQ(
              row?.caseQuantity,
              row?.otherUnitQuantity,
              row?.pieceQuantity,
              row?.caseToPieceQty,
              row?.otherToPieceQty,
              row?.minimumOrderQuantity
            ) && showMOQWarning;

          const { convertedCaseQty, convertedOtherQty, convertedPieceQty } =
            performUomConversion(
              0,
              0,
              row?.minimumOrderQuantity,
              row?.uom,
              row?.caseToPieceQty,
              row?.otherToPieceQty,
              0,
              0,
              0,
              true,
              row?.otherUnitName
            );
          
          const isFreeItem = row?.freeItem ?? false

          const {
            uomConversion,
            disableOtherUnitNameWiseCavinKair,
            displayOtherUnitName,
          } = ConfigDictionary;

          const disableOtherUom: boolean =
            (!row?.uom?.includes("all") && !row?.uom?.includes("other")) ||
            (uomConversion && row?.otherToPieceQty === 0) ||
            (disableOtherUnitNameWiseCavinKair &&
              row?.otherUnitName?.trim().length === 0) ||
              isFreeItem;
          const disableCaseUom: boolean =
            (!row?.uom?.includes("all") && !row?.uom?.includes("cs")) ||
            (uomConversion && row?.caseToPieceQty === 0) || isFreeItem;
          const disablePieceUom: boolean =
            (!row?.uom?.includes("all") && !row?.uom?.includes("ea")) || isFreeItem;

          let customOtherUnitLabel = displayOtherUnitName
            ? row?.otherUnitName && row?.otherUnitName.length > 0
              ? row?.otherUnitName
              : otherUnitLabel
            : otherUnitLabel;
          // Helper function to increment/decrement quantity
          const adjustQuantity = (
            currentValue: number,
            increment: number,
            maxDigits: number = 4
          ): number => {
            const newValue: number = Math.max(0, currentValue + increment);
            return newValue.toString().length <= maxDigits
              ? newValue
              : currentValue;
          };
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                cursor: !canEditOrder || isFreeItem
                  ? "not-allowed"
                  : isEditing
                  ? "default"
                  : "pointer",
                opacity: isFreeItem ? 0.6 : 1, // dim the whole section for free items
                pointerEvents: isFreeItem ? "none" : "auto", // block all clicks
              }}
              onClick={() => {
                if (!isFreeItem) handleEditClick();
              }}

            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  position: "relative",
                  width: "min-content",
                  gap: "4px",
                  pointerEvents: isEditing && !isFreeItem ? "auto" : "none",
                }}
              >
                {(quantityExceed || quantityBelowMOQ) && !isFreeItem && (
                  <DisclaimerIcon
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      zIndex: 1,
                      width: "12px",
                      height: "12px",
                    }}
                  />
                )}

                {/* Case Quantity Input */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    background: disableCaseUom
                      ? "#f5f5f5"
                      : quantityExceed || quantityBelowMOQ
                      ? "#FFF3F1" // warning state takes priority
                      : row?.hasChanged
                      ? "#F6FDFF" // changed highlight
                      : "#FAFAFB",

                    border: disableCaseUom
                      ? "1px solid #EEEEEE"
                      : quantityExceed || quantityBelowMOQ
                      ? "1px solid #FFE6E6" // warning border
                      : row?.hasChanged
                      ? "1px solid #80C3DB" // changed border
                      : "1px solid #EEEEEE",

                    borderRadius: "4px",
                    minHeight: "35px",
                    maxHeight: "35px",
                    padding: "0px 0px 0px 4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      style={{
                        width: `4ch`,
                        padding: "0px",
                        display: "inline-block",
                        textAlign: "start",
                        fontSize: "12px",
                        borderRadius: "3px",
                        color: disableCaseUom ? "#999" : "#7C7C7C",
                        outline: "none",
                        height: "auto", // don’t force input box height
                        boxShadow: "none", // remove default browser shadow
                        appearance: "none", // remove browser-specific styling
                        border: "none",
                        background: "transparent",
                      }}
                      disabled={disableCaseUom}
                      value={row?.caseQuantity}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (
                          /^\d{0,4}$/.test(inputValue) &&
                          !isNaN(+inputValue)
                        ) {
                          handleQuantityChange(row, +inputValue, UOM.CASE);
                        }
                      }}
                      onBlur={() => {
                        handleOnBlur(row);
                      }}
                    />

                    {/* Label */}
                    <span
                      style={{
                        fontSize: "11px",
                        color: disableCaseUom ? "#999" : "#666",
                        marginRight: "2px",
                      }}
                    >
                      {caseUnitLabel || "Cs"}
                    </span>
                  </div>

                  {/* Increment / Decrement buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: disableCaseUom ? 0.4 : 1, // fade when disabled
                      pointerEvents: disableCaseUom ? "none" : "auto", // block clicks
                      cursor: disableCaseUom ? "default" : "pointer",
                    }}
                  >
                    <ArrowDropUpRoundedIcon
                      fontSize="small"
                      viewBox="0 -6 24 24"
                      sx={{
                        cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                        pointerEvents: isEditing ? "auto" : "none",
                      }}
                      onClick={() => {
                        if (!disableCaseUom) {
                          const newValue: number = adjustQuantity(
                            row?.caseQuantity || 0,
                            1
                          );
                          updateQuantityAndBlur(row, newValue, UOM.CASE);
                        }
                      }}
                    />
                    <ArrowDropDownRoundedIcon
                      fontSize="small"
                      viewBox="0 6 24 24"
                      sx={{
                        cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                        pointerEvents: isEditing ? "auto" : "none",
                      }}
                      onClick={() => {
                        if (!disableCaseUom) {
                          const newValue: number = adjustQuantity(
                            row?.caseQuantity || 0,
                            -1
                          );
                          updateQuantityAndBlur(row, newValue, UOM.CASE);
                        }
                      }}
                    />
                  </div>
                </Box>

                {/* Other Unit Quantity Input */}
                {otherUnitLabel && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                      background: disableOtherUom
                        ? "#f5f5f5"
                        : quantityExceed || quantityBelowMOQ
                        ? "#FFF3F1" // warning state takes priority
                        : row?.hasChanged
                        ? "#F6FDFF" // changed highlight
                        : "#FAFAFB",

                      border: disableOtherUom
                        ? "1px solid #EEEEEE"
                        : quantityExceed || quantityBelowMOQ
                        ? "1px solid #FFE6E6" // warning border
                        : row?.hasChanged
                        ? "1px solid #80C3DB" // changed border
                        : "1px solid #EEEEEE",
                      borderRadius: "4px",
                      minHeight: "35px",
                      maxHeight: "35px",
                      padding: "0px 0px 0px 4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <input
                        style={{
                          padding: "0px",
                          width: `4ch`,

                          display: "inline-block",
                          textAlign: "start",
                          fontSize: "12px",
                          borderRadius: "3px",
                          color: disableOtherUom ? "#999" : "#7C7C7C",
                          outline: "none",
                          height: "auto", // inline-friendly
                          boxShadow: "none", // remove browser shadow
                          appearance: "none", // strip native styling
                          border: "none",
                          background: "transparent",
                        }}
                        disabled={disableOtherUom}
                        value={row?.otherUnitQuantity ?? 0}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (
                            /^\d{0,4}$/.test(inputValue) &&
                            !isNaN(+inputValue)
                          ) {
                            handleQuantityChange(row, +inputValue, UOM.OTHER);
                          }
                        }}
                        onBlur={(e) => {
                          let inputValue = e.target.value.trim();
                          if (inputValue === "") {
                            // user cleared → reset to 0
                            inputValue = "0";
                          }
                          if (
                            /^\d{0,4}$/.test(inputValue) &&
                            !isNaN(+inputValue)
                          ) {
                            handleQuantityChange(row, +inputValue, UOM.OTHER);
                          } else {
                            // reset invalid
                            e.target.value = String(
                              row?.otherUnitQuantity ?? 0
                            );
                          }
                          handleOnBlur(row);
                        }}
                      />

                      <span
                        style={{
                          fontSize: "11px",
                          color: disableOtherUom ? "#999" : "#666",
                          marginRight: "2px",
                        }}
                      >
                        {customOtherUnitLabel || "Oth"}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: disableOtherUom ? 0.4 : 1, // fade when disabled
                        pointerEvents: disableOtherUom ? "none" : "auto", // block clicks
                        cursor: disableOtherUom ? "default" : "pointer",
                      }}
                    >
                      <ArrowDropUpRoundedIcon
                        fontSize="small"
                        viewBox="0 -6 24 24"
                        sx={{
                          cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                          pointerEvents: isEditing ? "auto" : "none",
                        }}
                        onClick={() => {
                          if (!disableOtherUom) {
                            const newValue: number = adjustQuantity(
                              row?.otherUnitQuantity || 0,
                              1
                            );
                            updateQuantityAndBlur(row, newValue, UOM.OTHER);
                          }
                        }}
                      />
                      <ArrowDropDownRoundedIcon
                        fontSize="small"
                        viewBox="0 6 24 24"
                        sx={{
                        cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                          pointerEvents: isEditing ? "auto" : "none",
                        }}
                        onClick={() => {
                          if (!disableOtherUom) {
                            const newValue: number = adjustQuantity(
                              row?.otherUnitQuantity || 0,
                              -1
                            );
                            updateQuantityAndBlur(row, newValue, UOM.OTHER);
                          }
                        }}
                      />
                    </div>
                  </Box>
                )}

                {/* Piece Quantity Input */}

                {packUnitLabel && (

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    background: disablePieceUom
                      ? "#f5f5f5"
                      : quantityExceed || quantityBelowMOQ
                      ? "#FFF3F1" // warning state takes priority
                      : row?.hasChanged
                      ? "#F6FDFF" // changed highlight
                      : "#FAFAFB",

                    border: disablePieceUom
                      ? "1px solid #EEEEEE"
                      : quantityExceed || quantityBelowMOQ
                      ? "1px solid #FFE6E6" // warning border
                      : row?.hasChanged
                      ? "1px solid #80C3DB" // changed border
                      : "1px solid #EEEEEE",
                    borderRadius: "4px",
                    minHeight: "35px",
                    maxHeight: "35px",
                    padding: "0px 0px 0px 4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      style={{
                        padding: "0px",
                        width: `4ch`,
                        display: "inline-block",
                        textAlign: "start",
                        fontSize: "12px",
                        borderRadius: "3px",
                        color: disablePieceUom ? "#999" : "#7C7C7C",
                        outline: "none",
                        height: "auto",
                        boxShadow: "none", // remove browser shadow
                        appearance: "none", // remove native input styling
                        border: "none",
                        background: "transparent",
                      }}
                      disabled={disablePieceUom}
                      value={row?.pieceQuantity ?? 0}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (
                          /^\d{0,4}$/.test(inputValue) &&
                          !isNaN(+inputValue)
                        ) {
                          handleQuantityChange(row, +inputValue, UOM.PIECE);
                        }
                      }}
                      onBlur={(e) => {
                        let inputValue = e.target.value.trim();
                        if (inputValue === "") {
                          // user cleared → reset to 0
                          inputValue = "0";
                        }
                        if (
                          /^\d{0,4}$/.test(inputValue) &&
                          !isNaN(+inputValue)
                        ) {
                          handleQuantityChange(row, +inputValue, UOM.PIECE);
                        } else {
                          // reset invalid
                          e.target.value = String(row?.pieceQuantity ?? 0);
                        }
                        handleOnBlur(row);
                      }}
                    />

                    <span
                      style={{
                        fontSize: "11px",
                        color: disablePieceUom ? "#999" : "#666",
                        marginRight: "2px",
                      }}
                    >
                      {packUnitLabel}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: disablePieceUom ? 0.4 : 1, // faded when disabled
                      pointerEvents: disablePieceUom ? "none" : "auto", // block clicks when disabled
                      cursor: disablePieceUom ? "default" : "pointer",
                    }}
                  >
                    <ArrowDropUpRoundedIcon
                      viewBox="0 -6 24 24"
                      fontSize="small"
                      sx={{
                        cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                        pointerEvents: isEditing ? "auto" : "none",
                      }}
                      onClick={() => {
                        if (!disablePieceUom) {
                          const newValue: number = adjustQuantity(
                            row?.pieceQuantity || 0,
                            1
                          );
                          updateQuantityAndBlur(row, newValue, UOM.PIECE);
                        }
                      }}
                    />
                    <ArrowDropDownRoundedIcon
                      viewBox="0 6 24 24"
                      fontSize="small"
                      sx={{
                        cursor: !canEditOrder
                          ? "not-allowed"
                          : isEditing
                          ? "default"
                          : "pointer",
                        pointerEvents: isEditing ? "auto" : "none",
                      }}
                      onClick={() => {
                        if (!disablePieceUom) {
                          const newValue: number = adjustQuantity(
                            row?.pieceQuantity || 0,
                            -1
                          );
                          updateQuantityAndBlur(row, newValue, UOM.PIECE);
                        }
                      }}
                    />
                  </div>
                </Box>
                 )}

              </Box>
              {enableMOQCheck && <span style={{ color: "#DA892C", fontSize: "9px" }}>
                MOQ:
                <span style={{ color: "#C48236" }}>
                  {` ${convertedCaseQty} ${caseUnitLabel}, ${convertedPieceQty} ${packUnitLabel}, ${convertedOtherQty} ${otherUnitLabel}`}
                </span>
              </span>}
            </div>
          );
        },
      },

      ...(disableStockCheck || primaryOms
        ? []
        : [
            {
              headerName: "",
              mappedValue: "availableStock",
              width: isPartialOrder ? 200 : (columnsMetadataConfirguration?.["availableStock"]  ?? 140),
              renderHeader: (value: any) => <Box>Available Stock</Box>,
              renderCell: (value: any, row: any) => {
                const {
                  caseQuantity,
                  pieceQuantity,
                  otherUnitQuantity,
                  availableStock,
                  initialPieceQuantity,
                  initialCaseQuantity,
                  initialOtherUnitQuantity,
                  caseToPieceQty,
                  otherToPieceQty,
                  otherUnitName,
                } = row;

                const orderStatusPending = row?.status === "PENDING";
                let isExceedQty = false;
                const finalCaseStock =
                  availableStock?.cs +
                  (orderStatusPending ? 0 : initialCaseQuantity);
                const finalPieceStock =
                  availableStock?.pc +
                  (orderStatusPending ? 0 : initialPieceQuantity);
                const finalOtherStock =
                  availableStock?.other +
                  (orderStatusPending ? 0 : initialOtherUnitQuantity);

                const ConfigDictionary = JSON.parse(
                  localStorage.getItem("ConfigDictionary") ?? "{}"
                );
                if (ConfigDictionary.uomConversion) {
                  isExceedQty = checkIfStockExceeded(
                    caseQuantity,
                    otherUnitQuantity,
                    pieceQuantity,
                    finalCaseStock,
                    finalOtherStock,
                    finalPieceStock,
                    caseToPieceQty,
                    otherToPieceQty
                  );
                } else {
                  isExceedQty =
                    caseQuantity > finalCaseStock ||
                    pieceQuantity > finalPieceStock ||
                    otherUnitQuantity > finalOtherStock;
                }

                return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                  { isPartialOrder ? (

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                cursor: "default",
                opacity: 0.6, 
                pointerEvents: "none", 
              }}

            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  position: "relative",
                  width: "min-content",
                  gap: "4px",
                  pointerEvents: "none", 
                  "& > *": {
                    minWidth: "60px", // or width: "100px"
                  },
                }}
              >
               

              {/* Case Quantity Input */}  
              {caseUnitLabel && (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : "#FAFAFB",
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : "1px solid #EEEEEE",
      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={true}
        value={value?.cs +
                        (orderStatusPending ? 0 : initialCaseQuantity)}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {caseUnitLabel || "Cs"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "not-allowed",
        opacity: 0.6, 
        pointerEvents: "none", 
      }}
    >
    </div>
  </Box>
              )}


              {/* other unit label */}
              {otherUnitLabel && (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : "#FAFAFB",
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : "1px solid #EEEEEE",

      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={true}
        value={value?.other +
                        (orderStatusPending ? 0 : initialOtherUnitQuantity)}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {otherUnitLabelPrimary || "Oth"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "not-allowed",
        opacity: 0.6, 
        pointerEvents: "none", 
      }}
    >
    </div>
  </Box>
              )}

              {/* pack unit label */}
             { packUnitLabel && ( 
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : "#FAFAFB",
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : "1px solid #EEEEEE",
    
      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={true}
        value={value?.pc +
                        (orderStatusPending ? 0 : initialPieceQuantity)}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {packUnitLabel || "Pc"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "not-allowed",
        opacity: 0.6, 
        pointerEvents: "none", 
      }}
    >
    </div>
  </Box>
            )}              

              </Box>      
            </div>
                  ) : (<MultiQtyCell
                      pcQty={
                        value?.pc +
                        (orderStatusPending ? 0 : initialPieceQuantity)
                      }
                      csQty={
                        value?.cs +
                        (orderStatusPending ? 0 : initialCaseQuantity)
                      }
                      otherQty={
                        value?.other +
                        (orderStatusPending ? 0 : initialOtherUnitQuantity)
                      }
                      otherUnitName={otherUnitName}
                      alignLeft={true}
                      addDividers={true}
                    />)}
                  </div>
                  {isPartialOrder && enableRemainingStocksLabel && <span style={{ color: "#DA892C", fontSize: "9px" }}>
                  Remaining Stocks:
                  <span style={{ color: "#C48236" }}>
                    {` ${availableStock?.actualAvailableCaseQty} ${caseUnitLabel}, ${availableStock?.actualAvailablePieceQty} ${packUnitLabel}, ${availableStock?.actualAvailableOtherQty} ${otherUnitLabel}`}
                </span>
                </span>}
                </div>
                );
              },
            },
          ]),
      ...(primaryOms && showTotalConvertedCasesPrimaryOMS
        ? [
            {
              headerName: "totalConvertedCasesPrimaryOMS",
              mappedValue: "totalConvertedCasesPrimaryOMS",
              width: columnsMetadataConfirguration?.["totalConvertedCasesPrimaryOMS"]  ?? 80,
              textAlign: "center",
              renderHeader: () => (
                <div className="customTableInfo_flexColumnAlignCenter">
                  <div className="customTableInfo_flexColumnTextCenter">
                    <span>{"Total Qty"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">
                      {"(CS)"}
                    </span>
                  </div>
                </div>
              ),
              renderCell: (value: any) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div className="view_order_pack_quantity_input style_adjust">
                    {value}
                  </div>
                </Box>
              ),
            },
          ]
        : []),

      ...(!primaryOms && packUnitLabel
        ? [
            {
              headerName: ViewOrderEditTableHeader?.PIECEPRICE,
              mappedValue: "price",
              width: columnsMetadataConfirguration?.["price"]  ?? 80,
              textAlign: "left",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnTextLeft">
                  <span>{"Price"}</span>
                  <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${packUnitLabel})`}</span>
                </div>
              ),
              currencySymbol: true,
            },
          ]
        : [
            {
              headerName: ViewOrderEditTableHeader?.CASEPRICE,
              mappedValue: "casePrice",
              width: columnsMetadataConfirguration?.["casePrice"]  ?? 90,
              textAlign: "left",
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnTextLeft">
                  <span>{"Price"}</span>
                  <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${caseUnitLabel})`}</span>
                </div>
              ),
              currencySymbol: true,
            },
          ]),

      {
        headerName: ViewOrderEditTableHeader?.AMOUNT,
        mappedValue: "actualAmount",
        width: columnsMetadataConfirguration?.["actualAmount"]  ?? 100,
        textAlign: "left",
        currencySymbol: true,
        addAsterisk: true,
      },
      ...(hideItemLevelDiscountInfo ?
        [
          {
            headerName: ViewOrderEditTableHeader?.DISCOUNT,
            mappedValue: "discount",
            width: columnsMetadataConfirguration?.["discount"]  ?? 130,
            textAlign: "left",
            currencySymbol: true,
            addAsterisk: true,
          },
        ]:
        [{
        headerName: ViewOrderEditTableHeader?.DISCOUNT,
        mappedValue: "skuDiscountInfo",
        width: columnsMetadataConfirguration?.["skuDiscountInfo"]  ?? 100,
        textAlign: "left",
        addAsterisk: true,
        renderCell: (value: any, row: any) => {
          const aggregateDiscountAmount = value?.reduce(
            (acc: any, curr: any) => {
              acc += Number(curr?.discountAmount);
              return acc;
            },
            0
          );

          const consolidatedDiscountObj = {
            discountType: "All",
            discountAmount: aggregateDiscountAmount,
          };

          return (
            <FormControl className="manage_order_filter">
              <Select
                labelId="dropdown_label"
                size="small"
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    padding: "0px !important",
                  },
                  width: "100%",
                  fontSize: "12px",
                  padding: "0px",
                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-icon": {
                    display: value?.length === 0 ? "none" : "block",
                    marginRight: "-11px",
                    color: "#b0b0b0",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      ml: -1,
                      padding: 0,
                      boxShadow: "none",
                      border: "1px solid #DEDEDE",
                      borderRadius: "8px",
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: "7px 0",
                    },
                  },
                }}
                renderValue={() => {
                  return (
                    <span style={{ color: "#7c7c7c", fontSize: "12px" }}>
                      <span>
                        {`${currency ?? ""}\u200A${truncateToTwoDecimals(
                          consolidatedDiscountObj?.discountAmount ?? 0
                        )}`}
                      </span>
                    </span>
                  );
                }}
              >
                {value?.length > 0 &&
                  value?.map((item: any, index: any, arr: any) => (
                    <MenuItem
                      key={item?.discountType}
                      value={item?.discountType}
                      className="menuItem-test-debug"
                      sx={{
                        padding: "2px 0px",
                        borderBottom:
                          index !== arr.length - 1
                            ? "1.5px dashed #EBEBEB"
                            : "none",
                        margin: "0 10px",
                        pointerEvents: "none",
                      }}
                    >
                      <div className="loadout_dropdown_menu_item">
                        <span>
                          {`${item?.discountType ?? ""}: ${
                            currency ?? ""
                          }${truncateToTwoDecimals(item?.discountAmount ?? 0)}`}
                        </span>
                      </div>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          );
        },
      }]
      ),

      ...(hideItemLevelTaxInfo
        ? []
        : [
            {
              headerName: "Tax Amount & (%)",
              mappedValue: "taxInfo",
              width: columnsMetadataConfirguration?.["taxInfo"]  ?? 130,
              textAlign: "left",
              addAsterisk: true,
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignStart">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Tax Applicable"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">
                      {"Amount & (%)"}
                    </span>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const aggregateTaxAmount = value?.reduce(
                  (acc: any, curr: any) => {
                    acc += Number(curr?.taxAmount);
                    return acc;
                  },
                  0
                );
                const aggregateTaxPercent = value?.reduce(
                  (acc: any, curr: any) => {
                    acc += Number(curr?.taxPercent);
                    return acc;
                  },
                  0
                );

                const consolidatedTaxObj = {
                  taxType: "All",
                  taxAmount: aggregateTaxAmount,
                  taxPercent: aggregateTaxPercent,
                };

                return (
                  <FormControl className="manage_order_filter">
                    <Select
                      labelId="dropdown_label"
                      size="small"
                      displayEmpty
                      sx={{
                        "& .MuiSelect-select": {
                          padding: "0px !important",
                        },
                        width: "100%",
                        fontSize: "12px",
                        padding: "0px",
                        "&.Mui-focused": {
                          boxShadow: "none",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },

                        "& .MuiSelect-icon": {
                          display: value?.length === 0 ? "none" : "block",
                          marginRight: "-11px",
                          color: "#b0b0b0",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            padding: 0,
                            boxShadow: "none",
                            border: "1px solid #DEDEDE",
                            borderRadius: "8px",
                          },
                        },
                        MenuListProps: {
                          sx: {
                            padding: "7px 0",
                          },
                        },
                      }}
                      renderValue={() => {
                        return (
                          <span style={{ color: "#7c7c7c", fontSize: "12px" }}>
                            <span>
                              {`${currency ?? ""}\u200A${truncateToTwoDecimals(
                                consolidatedTaxObj?.taxAmount ?? 0
                              )}`}
                              {`(${consolidatedTaxObj?.taxPercent ?? 0}%)`}
                            </span>
                          </span>
                        );
                      }}
                    >
                      {value?.length > 0 &&
                        value?.map((item: any, index: any, arr: any) => (
                          <MenuItem
                            key={item?.taxType}
                            value={item?.taxType}
                            className="menuItem-test-debug"
                            sx={{
                              padding: "2px 0px",
                              borderBottom:
                                index !== arr.length - 1
                                  ? "1.5px dashed #EBEBEB"
                                  : "none",
                              margin: "0 10px",
                              pointerEvents: "none",
                            }}
                          >
                            <div className="loadout_dropdown_menu_item">
                              <span>
                                {`${item?.taxType ?? ""}: ${
                                  currency ?? ""
                                }${truncateToTwoDecimals(
                                  item?.taxAmount ?? 0
                                )}`}
                                {`(${item?.taxPercent ?? 0}%)`}
                              </span>
                            </div>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                );
              },
            },
          ]),


          ...(cokenpFlatTaxAmountField
            ? [
              {
                headerName: "Tax Amount",
                mappedValue: "taxAmountCokeNp",
                width: columnsMetadataConfirguration?.["taxAmountCokeNp"]  ?? 130,
                textAlign: "left",
                currencySymbol: true,
                addAsterisk: true,
              },
              ]
            : []),
      {
        headerName: ViewOrderEditTableHeader?.FINALAMOUNT,
        textAlign: "left",
        addAsterisk: true,
        renderHeader: (value: any) => (
          <Box
            display={"flex"}
            sx={{ flexDirection: "column", alignItems: "left" }}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              sx={{ textAlign: "left" }}
            >
              <span>{"Final"}</span>
              <span className="customTableInfo_marginTopNegative">
                {"Amount"}
              </span>
            </Box>
          </Box>
        ),
        mappedValue: "finalAmount",
        width: columnsMetadataConfirguration?.["finalAmount"]  ?? 100,
        renderCell: (value: any) => (
          <Box>
            <span
              style={{
                textAlign: "left",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {`${currency}\u200A${truncateToTwoDecimals(value) ?? ""}`}
            </span>
          </Box>
        ),
      },
      ...((isEditing && canEditOrder)
        ? [
            {
              headerName: "",
              mappedValue: "",
              width: 35,
              renderCell: (value: any, row: any) => {
                return (
                  <button
                    className="view_order_manage_order_table_button"
                    style={{ cursor: (isEditing && !row?.freeItem) ? "pointer" : "not-allowed" }}
                    onClick={() => {
                      if (row?.freeItem || !isEditing) return
                      handleRemove(row);
                    }}
                  >
                    <SubtractIcon style={{ width: "16px", height: "16px" }} />
                  </button>
                );
              },
            },
          ]
        : []),
    ],
  };

  return resultedColumns;
};


export const getViewOrderExpandableRowsConfig = (
  handleQuantityChange: (
    paramRow: any,
    changedQuantity: any,
    type: UOM
  ) => void,
  handleOnBlur: (paramRow: any) => void,
  handleRemove: (paramRow: any) => void,
  handlePromotionModalVisibility: (paramRow: viewManageOrderRowsData) => void,
  insideManageInvoice: boolean,
  ViewOrderEditTableHeader: Record<string, string>,
  disableStockCheck: boolean = false,
  hideItemLevelTaxInfo: boolean = false,
  primaryOms: boolean = false,
  enableMOQCheck: boolean = false,
  isEditing: boolean = false,
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
  updateQuantityAndBlur: (row: any, newValue: number, type: UOM) => void,
  handleEditClick: () => Promise<void>,
  canEditOrder: boolean,
) => {
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const {
    showTotalConvertedCasesPrimaryOMS,
    otherUnitLabelPrimary,
    batchIdConfiguration = false,
  } = ConfigDictionary;

  return {
    columns: [
      ...(insideManageInvoice && batchIdConfiguration
        ? [
            {
              headerName: "Batch Id",
              mappedValue: "batchId",
              width: 130,
              renderHeader: (value: any) => (
                <Box display={"flex"} flexDirection={"column"}>
                  <span>{"Batch ID"}</span>
                </Box>
              ),
              renderCell: (value: any, row: any) => {
                return(
                  <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>

                  
                  <Tooltip title={value}>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: "12px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        textAlign: "left"
                      }}
                    >
                      {value}
                    </span>
                  </Tooltip>
                  {(Array.isArray(row?.discountInfo) &&
                  row.discountInfo.length !== 0) &&
                  <div
                    className="view-discounts-container"
                    
                    onClick={() => handlePromotionModalVisibility(row)}
                  >
                    <span style={{
                      fontSize: "9px"
                    }}>View Promos</span>
                    <CircledRight style={{ width: "8px" }} />
                  </div>}
                  </div>
                );
              },
            },
          ]
        : []),

      {
        headerName: "Ordered Quantity",
        width: 140,
        textAlign: "center",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignStart">
            <div className="customTableInfo_flexColumnTextCenter">
              <span>{"Ordered Quantity"}</span>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <MultiQtyCell
                pcQty={
                  row?.initialPieceQuantity
                }
                csQty={
                  row?.initialCaseQuantity
                }
                otherQty={
                  row?.initialOtherUnitQuantity
                }
                otherUnitName={row?.otherUnitName}
                alignLeft={true}
                addDividers={true}
              />
          </div>
          );
        },
      },
      ...((!primaryOms && packUnitLabel) ? [{
        headerName: ViewOrderEditTableHeader?.PIECEPRICE,
        mappedValue: "price",
        width: 80,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnTextLeft">
            <span>{"Price"}</span>
            <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${packUnitLabel})`}</span>
          </div>
        ),
        currencySymbol: true,
      }] : [{
        headerName: ViewOrderEditTableHeader?.CASEPRICE,
        mappedValue: "casePrice",
        width: 90,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnTextLeft">
            <span>{"Price"}</span>
            <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${caseUnitLabel})`}</span>
          </div>
        ),
        currencySymbol: true,
      }]),
      {
        headerName: ViewOrderEditTableHeader?.AMOUNT,
        mappedValue: "actualAmount",
        width: 100,
        textAlign: "left",
        currencySymbol: true,
        addAsterisk: true,
      },
      {
        headerName: ViewOrderEditTableHeader?.DISCOUNT,
        mappedValue: "skuDiscountInfo",
        width: 130,
        textAlign: "left",
        addAsterisk: true,
        renderCell: (value: any, row: any) => {
          const aggregateDiscountAmount = value?.reduce(
            (acc: any, curr: any) => {
              acc += Number(curr?.discountAmount);
              return acc;
            },
            0
          );

          const consolidatedDiscountObj = {
            discountType: "All",
            discountAmount: aggregateDiscountAmount,
          };

          return (
            <FormControl className="manage_order_filter">
              <Select
                labelId="dropdown_label"
                size="small"
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    padding: "0px !important",
                  },
                  width: "100%",
                  fontSize: "12px",
                  padding: "0px",
                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-icon": {
                    display: value?.length === 0 ? "none" : "block",
                    marginRight: "-11px",
                    color: "#b0b0b0",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      ml: -1,
                      padding: 0,
                      boxShadow: "none",
                      border: "1px solid #DEDEDE",
                      borderRadius: "8px",
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: "7px 0",
                    },
                  },
                }}
                renderValue={() => {
                  return (
                    <span style={{ color: "#7c7c7c", fontSize: "12px" }}>
                      <span>
                        {`${currency ?? ""}\u200A${truncateToTwoDecimals(
                          consolidatedDiscountObj?.discountAmount ?? 0
                        )}`}
                      </span>
                    </span>
                  );
                }}
              >
                {value?.length > 0 &&
                  value?.map((item: any, index: any, arr: any) => (
                    <MenuItem
                      key={item?.discountType}
                      value={item?.discountType}
                      className="menuItem-test-debug"
                      sx={{
                        padding: "2px 0px",
                        borderBottom:
                          index !== arr.length - 1
                            ? "1.5px dashed #EBEBEB"
                            : "none",
                        margin: "0 10px",
                        pointerEvents: "none",
                      }}
                    >
                      <div className="loadout_dropdown_menu_item">
                        <span>
                          {`${item?.discountType ?? ""}: ${
                            currency ?? ""
                          }${truncateToTwoDecimals(item?.discountAmount ?? 0)}`}
                        </span>
                      </div>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          );
        },
      },
      ...(hideItemLevelTaxInfo
        ? []
        : [
            {
              headerName: "Tax Amount & (%)",
              mappedValue: "taxInfo",
              width: 160,
              textAlign: "left",
              addAsterisk: true,
              renderHeader: (value: any) => (
                <div className="customTableInfo_flexColumnAlignStart">
                  <div className="customTableInfo_flexColumnTextLeft">
                    <span>{"Tax Applicable"}</span>
                    <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">
                      {"Amount & (%)"}
                    </span>
                  </div>
                </div>
              ),
              renderCell: (value: any, row: any) => {
                const aggregateTaxAmount = value?.reduce(
                  (acc: any, curr: any) => {
                    acc += Number(curr?.taxAmount);
                    return acc;
                  },
                  0
                );
                const aggregateTaxPercent = value?.reduce(
                  (acc: any, curr: any) => {
                    acc += Number(curr?.taxPercent);
                    return acc;
                  },
                  0
                );

                const consolidatedTaxObj = {
                  taxType: "All",
                  taxAmount: aggregateTaxAmount,
                  taxPercent: aggregateTaxPercent,
                };

                return (
                  <FormControl className="manage_order_filter">
                    <Select
                      labelId="dropdown_label"
                      size="small"
                      displayEmpty
                      sx={{
                        "& .MuiSelect-select": {
                          padding: "0px !important",
                        },
                        width: "100%",
                        fontSize: "12px",
                        padding: "0px",
                        "&.Mui-focused": {
                          boxShadow: "none",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },

                        "& .MuiSelect-icon": {
                          display: value?.length === 0 ? "none" : "block",
                          marginRight: "-11px",
                          color: "#b0b0b0",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            padding: 0,
                            boxShadow: "none",
                            border: "1px solid #DEDEDE",
                            borderRadius: "8px",
                          },
                        },
                        MenuListProps: {
                          sx: {
                            padding: "7px 0",
                          },
                        },
                      }}
                      renderValue={() => {
                        return (
                          <span style={{ color: "#7c7c7c", fontSize: "12px" }}>
                            <span>
                              {`${currency ?? ""}\u200A${truncateToTwoDecimals(
                                consolidatedTaxObj?.taxAmount ?? 0
                              )}`}
                              {`(${consolidatedTaxObj?.taxPercent ?? 0}%)`}
                            </span>
                          </span>
                        );
                      }}
                    >
                      {value?.length > 0 &&
                        value?.map((item: any, index: any, arr: any) => (
                          <MenuItem
                            key={item?.taxType}
                            value={item?.taxType}
                            className="menuItem-test-debug"
                            sx={{
                              padding: "2px 0px",
                              borderBottom:
                                index !== arr.length - 1
                                  ? "1.5px dashed #EBEBEB"
                                  : "none",
                              margin: "0 10px",
                              pointerEvents: "none",
                            }}
                          >
                            <div className="loadout_dropdown_menu_item">
                              <span>
                                {`${item?.taxType ?? ""}: ${
                                  currency ?? ""
                                }${truncateToTwoDecimals(
                                  item?.taxAmount ?? 0
                                )}`}
                                {`(${item?.taxPercent ?? 0}%)`}
                              </span>
                            </div>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                );
              },
            },
          ]),
      {
        headerName: ViewOrderEditTableHeader?.FINALAMOUNT,
        textAlign: "left",
        addAsterisk: true,
        renderHeader: (value: any) => (
          <Box
            display={"flex"}
            sx={{ flexDirection: "column", alignItems: "left" }}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              sx={{ textAlign: "left" }}
            >
              <span>{"Final"}</span>
              <span className="customTableInfo_marginTopNegative">
                {"Amount"}
              </span>
            </Box>
          </Box>
        ),
        mappedValue: "finalAmount",
        width: 100,
        renderCell: (value: any) => (
          <Box>
            <span
              style={{
                textAlign: "left",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: "#7C7C7C"
              }}
            >
              {`${currency}\u200A${truncateToTwoDecimals(value) ?? ""}`}
            </span>
          </Box>
        ),
      },
    ],
  };
};

export const metaData2: any[] = [
  {
    keys: "outletCode",
    label: "Outlet Code",
    index: 1,
  },
  {
    keys: "salesRepId",
    label: "SalesRep Code",
    index: 5,
  },
  {
    keys: "supplier",
    label: "Distributor Code",
    index: 3,
  },
  {
    keys: "outletName",
    label: "Outlet Name",
    index: 2,
  },
  {
    keys: "salesRepName",
    label: "SalesRep Name",
    index: 6,
  },
  {
    keys: "supplierName",
    label: "Distributor Name",
    index: 4,
  }, 
];

export const getViewPrimaryOrderOmsConfigV2Final = (
  handleRemove: (paramRow: any) => void,
  showCompanyStockOMS: boolean = false,
  siteWarehouseMapping: any = {},
  handleSkuLevelSiteChange: (row: any, siteId: string) => void,
  handleSkuLevelWarehouseChange: (row: any, warehouseId: string) => void,
  orderStatus:string,
  // canEditOrder: boolean,
  // isEditing: boolean = false,
  // handleEditClick: () => Promise<void>,
  handleQuantityChange: (paramRow: any, changedQuantity: any, type: UOM) => void,
  handleClone: (paramRow: any) => void,
  handleSkuReplace: (row: any, newBatchCode: string, newSkuDescription: string) => void
) => {

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {showTotalConvertedCasesPrimaryOMS,  otherUnitLabelPrimary,caseUnitLabelPrimary,packUnitLabelPrimary,currency} = ConfigDictionary

  const status = orderStatus?.toUpperCase()

  return {
    columns: [
      // {
      //   headerName: "skuNameAndCode",
      //   mappedValue: "batchCode",
      //   width: 150,
      //   renderHeader: (value: any) => (
      //     <Box display="flex" sx={{ flexDirection: "column", alignItems: "left" }}>
      //               <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
      //                 <span>{"SKU Name"}</span>
      //                 <span className="customTableInfo_marginTopNegative">{"& Code"}</span>
      //               </Box>
      //             </Box>
      //   ),
      //   renderCell: (value: any, row: any) => {
      //     return (
      //       <Tooltip title={`${row?.skuDescription} - ${value}`}>
      //         <Box className="sku_name_truncate_text ViewOrderConfig_skuCode">
      //           {row?.freeItem && (
      //             <span className="ViewOrderConfig_freeText">FREE</span>
      //           )}
      //           {row?.isNew && (
      //             <span style={{ color: "#cf2a90",
      //               fontSize: "8px",fontWeight: "700"}}>NEW</span>
      //           )}
      //           <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      //             <span style={{ ...TruncatedTextUptoLine(1), fontWeight: 500, fontSize: "13px" }}>
      //               {row?.skuDescription}
      //             </span>
      //             <span style={{ ...TruncatedTextUptoLine(1), fontSize: "12px", color: "#666" }}>
      //               {value}
      //             </span>
      //           </div>
      //         </Box>
      //       </Tooltip>
      //     );
      //   },
      // },

      {
        headerName: "skuNameAndCode",
        mappedValue: "batchCode",
        width: 170,
        renderHeader: (value: any) => (
          <Box display="flex" sx={{ flexDirection: "column", alignItems: "left" }}>
            <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
              <span>{"SKU Name"}</span>
              <span className="customTableInfo_marginTopNegative">{"& Code"}</span>
            </Box>
          </Box>
        ),
        renderCell: (value: any, row: any) => {
          if (status !== "PENDING" || row?.freeItem || row?.alreadySplitted) {
            // READ-ONLY DISPLAY
            return (
              <Tooltip title={`${row?.skuDescription} - ${value}`}>
                <Box className="sku_name_truncate_text ViewOrderConfig_skuCode">
                  {row?.freeItem && (
                    <span className="ViewOrderConfig_freeText">FREE</span>
                  )}
                  {row?.isNew && (
                    <span style={{ color: "#cf2a90", fontSize: "8px", fontWeight: "700" }}>
                      NEW
                    </span>
                  )}
                    {row?.alreadySplitted && (
                    <span style={{ color: "#cf2a90", fontSize: "8px", fontWeight: "700" }}>
                      SPLITTED
                    </span>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ ...TruncatedTextUptoLine(1), fontWeight: 500, fontSize: "13px" }}>
                      {row?.skuDescription}
                    </span>
                    <span style={{ ...TruncatedTextUptoLine(1), fontSize: "12px", color: "#666" }}>
                      {value}
                    </span>
                  </div>
                </Box>
              </Tooltip>
            );
          }

          // DROPDOWN FOR PENDING STATUS
          return (
            <FormControl variant="outlined" sx={{ width: "100%" }}>
              <Select
              disabled={row?.isChecked  ||  row?.alreadySplitted}
                value={value ?? ""}
                onChange={(e) => {
                  const newBatchCode = e.target.value;
                  const selectedSku = row?.replacementSkuList?.find(
                    (sku: any) => sku.batchCode === newBatchCode
                  );
                  if (selectedSku) {
                    handleSkuReplace(row, newBatchCode, selectedSku?.skuDescription ?? "");
                  }
                }}
                size="small"
          displayEmpty
          sx={{
            padding: "1px 0px 2px",
            fontSize: "11px",
            backgroundColor: "#Fafafa", // Gray background
            borderRadius: "8px", // Rounded corners
            "& .MuiSelect-select": { 
              padding: "2px 6px",
              backgroundColor: "#F5F5F5", // Gray background for select
              borderRadius: "8px", // Rounded corners for select
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D0D0D0", // Darker gray border
              borderRadius: "8px", // Rounded corners for border
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B0B0B0", // Even darker on hover
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#999999", // Darkest on focus
            },
               
                }}
                renderValue={(selected) => (
                  <Tooltip title={`${row?.skuDescription} - ${value}`}>
                    <Box className="sku_name_truncate_text ViewOrderConfig_skuCode">
                      {/* {row?.isNew && (
                        <span style={{ color: "#cf2a90", fontSize: "8px", fontWeight: "700" }}>
                          NEW
                        </span>
                      )} */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ ...TruncatedTextUptoLine(1), fontWeight: 500, fontSize: "11px" }}>
                          {row?.skuDescription}
                        </span>
                        <span style={{ ...TruncatedTextUptoLine(1), fontSize: "10px", color: "#666" }}>
                          {value}
                        </span>
                      </div>
                    </Box>
                  </Tooltip>
                )}
              >
                {row?.replacementSkuList?.length > 0 ? (
                  row.replacementSkuList.map((sku: any) => (
                    <MenuItem key={sku.batchCode} value={sku.batchCode} sx={{ fontSize: "12px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>{sku.description}</span>
                        <span style={{ fontSize: "11px", color: "#666" }}>{sku.batchCode}</span>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ fontSize: "11px" }}>
                    No replacement SKUs available
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          );
        },
      },

   // site
{
  headerName: "Site",
  mappedValue: "siteAllSku",
  width: 125,
  renderHeader: () => (
    <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
      <span>{"Site"}</span>
    </Box>
  ),
  renderCell: (value: any, row: any) => {
    if (status !== "PENDING"  || row?.freeItem) {
      // READ-ONLY DISPLAY LIKE SKU CODE
      return (
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "13px" }}>
          {value || "-"}
        </Box>
      );
    }

    // EXISTING DROPDOWN UI
    return (
      <FormControl variant="outlined" sx={{ width: "80%" }}>
        <Select
          disabled={row?.isChecked ||  row?.alreadySplitted}
          value={value ?? ""}
          onChange={(e) => {
            const newValue = e.target.value;
            const isValidOption = Object.keys(siteWarehouseMapping).includes(newValue);
            if (isValidOption) {
              handleSkuLevelSiteChange(row, newValue);
              handleSkuLevelWarehouseChange(row, ""); // Reset warehouse
            }
          }}
          size="small"
          displayEmpty
          sx={{
            padding: "1px 0px 2px",
            fontSize: "11px",
            backgroundColor: "#F5F5F5", // Gray background
            borderRadius: "8px", // Rounded corners
            "& .MuiSelect-select": { 
              padding: "4px 8px",
              backgroundColor: "#F5F5F5", // Gray background for select
              borderRadius: "8px", // Rounded corners for select
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D0D0D0", // Darker gray border
              borderRadius: "8px", // Rounded corners for border
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B0B0B0", // Even darker on hover
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#999999", // Darkest on focus
            },
          }}
          renderValue={(selected) => (selected ? selected : "Select Site")}
        >
          <MenuItem value="" disabled>Select Site</MenuItem>
          {Object.keys(siteWarehouseMapping).map((siteId) => (
            <MenuItem key={siteId} value={siteId} sx={{ fontSize: "11px" }}>
              {siteId}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
},

// warehouse
{
  headerName: "Warehouse",
  mappedValue: "warehouseAllSku",
  width: 125,
  renderHeader: () => (
    <Box display="flex" flexDirection="column" sx={{ textAlign: "left" }}>
      <span>{"Warehouse"}</span>
    </Box>
  ),
  renderCell: (value: any, row: any) => {
    if (status !== "PENDING"  || row?.freeItem) {
      // READ-ONLY DISPLAY LIKE SKU CODE
      return (
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "13px" }}>
          {value || "-"}
        </Box>
      );
    }

    // EXISTING DROPDOWN UI
    return (
      <FormControl variant="outlined" sx={{ width: "80%" }}>
        <Select
          value={value ?? ""}
          onChange={(e) => {
            const newValue = e.target.value;
            const validWarehouses = siteWarehouseMapping[row.siteAllSku] || [];
            const isValidOption = validWarehouses.includes(newValue);
            if (isValidOption) {
              handleSkuLevelWarehouseChange(row, newValue);
            }
          }}
          size="small"
          displayEmpty
          sx={{
            padding: "1px 0px 2px",
            fontSize: "11px",
            backgroundColor: "#F5F5F5", // Gray background
            borderRadius: "8px", // Rounded corners
            "& .MuiSelect-select": { 
              padding: "4px 8px",
              backgroundColor: "#F5F5F5", // Gray background for select
              borderRadius: "8px", // Rounded corners for select
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D0D0D0", // Darker gray border
              borderRadius: "8px", // Rounded corners for border
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B0B0B0", // Even darker on hover
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#999999", // Darkest on focus
            },
          }}
          renderValue={(selected) => (selected ? selected : "Select Warehouse")}
          disabled={!row.siteAllSku || !siteWarehouseMapping[row.siteAllSku]?.length || row?.isChecked ||  row?.alreadySplitted}
        >
          <MenuItem value="" disabled>Select Warehouse</MenuItem>
          {(siteWarehouseMapping[row?.siteAllSku] || []).map((warehouseId: string) => (
            <MenuItem key={warehouseId} value={warehouseId} sx={{ fontSize: "11px" }}>
              {warehouseId}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
},

      {
        headerName: "Ordered Quantity",
        width: 150,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignStart">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Ordered Quantity"}</span>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {
          
          const isFreeItem = row?.freeItem ?? false

          const adjustQuantity = (
            currentValue: number,
            increment: number,
            maxDigits: number = 4
          ): number => {
            const newValue: number = Math.max(0, currentValue + increment);
            return newValue.toString().length <= maxDigits
              ? newValue
              : currentValue;
          };

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                cursor: "default",
                opacity: isFreeItem || status != "PENDING" || row?.isChecked ||  row?.alreadySplitted ? 0.6 : 1, 
                pointerEvents: isFreeItem || status !="PENDING" || row?.isChecked  ||  row?.alreadySplitted? "none" : "auto", 
              }}

            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  position: "relative",
                  width: "min-content",
                  gap: "4px",
                  pointerEvents: isFreeItem || status !="PENDING" || row?.isChecked ||  row?.alreadySplitted ? "none" : "auto", 
                }}
              >
               

              {/* Case Quantity Input */}  
              {caseUnitLabelPrimary && (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : (row?.hasChanged ? "#F6FDFF" : "#FAFAFB"),
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : (row?.hasChanged ? "1px solid #80C3DB"  : "1px solid #EEEEEE"),
      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted}
        value={row?.caseQuantity}
        onChange={(e) => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const inputValue = e.target.value;
          if (
            /^\d{0,4}$/.test(inputValue) &&
            !isNaN(+inputValue)
          ) {
            handleQuantityChange(row, +inputValue, UOM.CASE);
          }
        }}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {caseUnitLabelPrimary || "Cs"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity:  1,
        pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
      }}
    >
      <ArrowDropUpRoundedIcon
        fontSize="small"
        viewBox="0 -6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted)  ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const newValue: number = adjustQuantity(
            row?.caseQuantity || 0,
            1
          );
          handleQuantityChange(row, +newValue, UOM.CASE);
        }}
      />
      <ArrowDropDownRoundedIcon
        fontSize="small"
        viewBox="0 6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const newValue: number = adjustQuantity(
            row?.caseQuantity || 0,
            -1
          );
          handleQuantityChange(row, +newValue, UOM.CASE);
        }}
      />
    </div>
  </Box>
              )}


              {/* other unit label */}
              {otherUnitLabelPrimary && (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : (row?.hasChanged ? "#F6FDFF" : "#FAFAFB"),
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : (row?.hasChanged ? "1px solid #80C3DB"  : "1px solid #EEEEEE"),

      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted}
        value={row?.otherUnitQuantity ?? 0}
        onChange={(e) => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const inputValue = e.target.value;
          if (
            /^\d{0,4}$/.test(inputValue) &&
            !isNaN(+inputValue)
          ) {
            handleQuantityChange(row, +inputValue, UOM.OTHER);
          }
        }}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {otherUnitLabelPrimary || "Oth"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity:  1,
        pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
      }}
    >
      <ArrowDropUpRoundedIcon
        fontSize="small"
        viewBox="0 -6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const newValue: number = adjustQuantity(
            row?.otherUnitQuantity || 0,
            1
          );
          handleQuantityChange(row, +newValue, UOM.OTHER);
        }}
      />
      <ArrowDropDownRoundedIcon
        fontSize="small"
        viewBox="0 6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const newValue: number = adjustQuantity(
            row?.otherUnitQuantity || 0,
            -1
          );
          handleQuantityChange(row, +newValue, UOM.OTHER);
        }}
      />
    </div>
  </Box>
              )}

              {/* pack unit label */}
             { packUnitLabelPrimary && ( 
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      position: "relative",
      background: (row?.isNew && (row?.caseQuantity ==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0))  ? "#fff3f1" : (row?.hasChanged ? "#F6FDFF" : "#FAFAFB"),
      border: (row?.isNew && (row?.caseQuantity==0 && row?.otherUnitQuantity==0 && row?.pieceQuantity==0) )  ? "#fff3f1" : (row?.hasChanged ? "1px solid #80C3DB"  : "1px solid #EEEEEE"),
    
      borderRadius: "4px",
      minHeight: "35px",
      maxHeight: "35px",
      padding: "0px 0px 0px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <input
        style={{
          width: `4ch`,
          padding: "0px",
          display: "inline-block",
          textAlign: "start",
          fontSize: "12px",
          borderRadius: "3px",
          color: "#7C7C7C",
          outline: "none",
          height: "auto",
          boxShadow: "none",
          appearance: "none",
          border: "none",
          background: "transparent",
        }}
        disabled={orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted}
        value={row?.pieceQuantity}
        onChange={(e) => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const inputValue = e.target.value;
          if (
            /^\d{0,4}$/.test(inputValue) &&
            !isNaN(+inputValue)
          ) {
            handleQuantityChange(row, +inputValue, UOM.PIECE);
          }
        }}
      />

      {/* Label */}
      <span
        style={{
          fontSize: "11px",
          color: "#666",
          marginRight: "2px",
        }}
      >
        {packUnitLabelPrimary || "Pc"}
      </span>
    </div>

    {/* Increment / Decrement buttons */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity:  1,
        pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
      }}
    >
      <ArrowDropUpRoundedIcon
        fontSize="small"
        viewBox="0 -6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked) return;
          const newValue: number = adjustQuantity(
            row?.pieceQuantity || 0,
            1
          );
          handleQuantityChange(row, +newValue, UOM.PIECE);
        }}
      />
      <ArrowDropDownRoundedIcon
        fontSize="small"
        viewBox="0 6 24 24"
        sx={{
          cursor: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) ? "not-allowed" : "pointer",
          pointerEvents: (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted)  ? "none" : "auto",
        }}
        onClick={() => {
          if (orderStatus !== "PENDING" || row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return;
          const newValue: number = adjustQuantity(
            row?.pieceQuantity || 0,
            -1
          );
          handleQuantityChange(row, +newValue, UOM.PIECE);
        }}
      />
    </div>
  </Box>
            )}              

              </Box>      
            </div>
          );
        },
      },


        ...(showTotalConvertedCasesPrimaryOMS
          ? [
              {
                headerName: "totalConvertedCasesPrimaryOMS",
                mappedValue: "totalConvertedCasesPrimaryOMS",
                width: 80,
                textAlign: "center",
                renderHeader: () => (
                  <div className="customTableInfo_flexColumnAlignCenter">
                    <div className="customTableInfo_flexColumnTextCenter">
                      <span>{"Total Qty"}</span>
                      <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(${caseUnitLabelPrimary})`}</span>
                    </div>
                  </div>
                ),
                renderCell: (value: any) => (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="view_order_pack_quantity_input style_adjust">{value}</div>
                  </Box>
                ),
              },
            ]
          : []),


       ...(showCompanyStockOMS
       ? [
           {
             headerName: "Company Stock",
             mappedValue: "availableStock",
             width: 110,
            renderHeader: () => (
            <Box display="flex" flexDirection="column" sx={{ textAlign: "left", alignItems: "flex-start" }}>
              <span>{"Available"}</span>
              <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">
              {"Quantity"}
              </span>
            </Box>
          ),

            renderCell: (value: any,row:any) => {
           
              return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                  <MultiQtyCell
                    pcQty={0}
                    csQty={value?.cs }
                    otherQty={0}
                    viewprimaryorderconfigs={true}
                    singleQtyUom={caseUnitLabelPrimary}
                  />
                </div>
              );
            },
            
           },
         ]
       : []),


      {
        headerName:  `Price (Each ${caseUnitLabelPrimary})`,
        mappedValue: "casePrice",
        width: 90,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnTextLeft">
            <span>{"Price"}</span>
            <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{`(Each ${caseUnitLabelPrimary})`}</span>
          </div>
        ),
        currencySymbol: true,
      },
      
      {
        headerName: "Amount",
        mappedValue: "actualAmount",
        width: 115,
        textAlign: "left",
        currencySymbol: true,
      },
      {
        headerName: "Discount",
        mappedValue: "skuDiscountInfo",
        width: 110,
        textAlign: "left",
        renderCell: (value: any, row: any) => {

          const aggregateDiscountAmount = value?.reduce(
            (acc: any, curr: any) => {
              acc += Number(curr?.discountAmount);
              return acc;
            },
            0
          );

          const consolidatedDiscountObj = {
            discountType: "All",
            discountAmount: aggregateDiscountAmount,
          }

          return (
            <FormControl className="manage_order_filter">
            <Select
              labelId="dropdown_label"
              size="small"
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  padding: "0px !important",
                },
                width: "100%",
                fontSize: "14px",
                padding: "0px",
                "&.Mui-focused": {
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-icon": {
                  display: value?.length === 0 ? "none" : "block",
                  marginRight: "-11px",
                  color: "#b0b0b0"
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    ml: -1,
                    padding: 0,
                    boxShadow: "none",
                    border: "1px solid #DEDEDE",
                    borderRadius: "8px",
                  },
                },
                MenuListProps: {
                  sx: {
                    padding: "7px 0",
                  },
                },
              }}
              renderValue={() => {
                return (
                  <span style={{ color: "#7c7c7c", fontSize: "13px" }}>
                    <span>
                      {`${
                        currency ?? ""
                      }\u200A${truncateToTwoDecimals(consolidatedDiscountObj?.discountAmount ?? 0)}`}
                    </span>
                  </span>
                );
              }}
            >
              {value?.length > 0 && value?.map((item: any, index: any, arr: any) => (
                <MenuItem
                  key={item?.discountType}
                  value={item?.discountType}
                  className="menuItem-test-debug"
                  sx={{
                    padding: "2px 0px",
                    borderBottom:
                      index !== arr.length - 1
                        ? "1.5px dashed #EBEBEB"
                        : "none",
                    margin: "0 10px",
                    pointerEvents: "none"
                  }}
                >
                  <div className="loadout_dropdown_menu_item">
                  <span>
                {`${item?.discountType ?? ""}: ${currency ?? ""}${truncateToTwoDecimals(item?.discountAmount ?? 0)}`}
              </span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          
          );
        },
      },


     {
        headerName: "Tax Amount & (%)",
        mappedValue: "taxInfo",
        width: 150,
        textAlign: "left",
        renderHeader: (value: any) => (
          <div className="customTableInfo_flexColumnAlignStart">
            <div className="customTableInfo_flexColumnTextLeft">
              <span>{"Tax Applicable"}</span>
              <span className="customTableInfo_footerOrderLightFont customTableInfo_marginTopNegative">{"Amount & (%)"}</span>
            </div>
          </div>
        ),
        renderCell: (value: any, row: any) => {

          const aggregateTaxAmount = value?.reduce((acc: any, curr: any) => {
            acc += Number(curr?.taxAmount);
            return acc;
          }, 0);
          const aggregateTaxPercent = value?.reduce((acc: any, curr: any) => {
            acc += Number(curr?.taxPercent);
            return acc;
          }, 0);

          const consolidatedTaxObj = {
            taxType: "All",
            taxAmount: aggregateTaxAmount,
            taxPercent: aggregateTaxPercent,
          };

          return (
            <FormControl className="manage_order_filter">
            <Select
              labelId="dropdown_label"
              size="small"
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  padding: "0px !important",
                },
                width: "100%",
                fontSize: "14px",
                padding: "0px",
                "&.Mui-focused": {
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                
                "& .MuiSelect-icon": {
                  display: value?.length === 0 ? "none" : "block",
                  marginRight: "-11px",
                  color: "#b0b0b0"
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    padding: 0,
                    boxShadow: "none",
                    border: "1px solid #DEDEDE",
                    borderRadius: "8px",
                  },
                },
                MenuListProps: {
                  sx: {
                    padding: "7px 0",
                  },
                },
              }}
              renderValue={() => {
                return (
                  <span style={{ color: "#7c7c7c", fontSize: "13px" }}>
                   <span>
                {`${
                  currency ?? ""
                }\u200A${truncateToTwoDecimals(consolidatedTaxObj?.taxAmount ?? 0)}`}
                {`(${consolidatedTaxObj?.taxPercent ?? 0}%)`}
              </span>
                  </span>
                );
              }}
            >
              
              {value?.length > 0 && value?.map((item: any, index: any, arr: any) => (
                <MenuItem
                  key={item?.taxType}
                  value={item?.taxType}
                  className="menuItem-test-debug"
                  sx={{
                    padding: "2px 0px",
                    borderBottom:
                      index !== arr.length - 1
                        ? "1.5px dashed #EBEBEB"
                        : "none",
                    margin: "0 10px",
                    pointerEvents: "none"
                  }}
                >
                  <div className="loadout_dropdown_menu_item">
                  <span>
                {`${item?.taxType ?? ""}: ${currency ?? ""}${truncateToTwoDecimals(item?.taxAmount ?? 0)}`}
                {`(${item?.taxPercent ?? 0}%)`}
              </span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          );
        },
      },


      {
        headerName: "Final Amount",
        textAlign: "left",
        renderHeader: (value: any) => (
          <Box display={"flex"} sx={{ flexDirection: "column", alignItems: "left" }}>
            <Box display={"flex"} flexDirection={"column"} sx={{ textAlign: "left" }}>
              <span>{"Final"}</span>
              <span className="customTableInfo_marginTopNegative">{"Amount"}</span>
            </Box>
          </Box>
        ),
        mappedValue: "finalAmount",
        width: 100,
        renderCell: (value: any) => (
          <Box>
            <span
              style={{
                textAlign: "left",
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {`${currency}\u200A${truncateToTwoDecimals(value) ?? ""}`}
            </span>
          </Box>
        ),
      },

      {
        headerName: "",
        width: 40,
        renderCell: (value: any, row: any) =>
          row?.isNew ? (
            <span style={{ color: "#cf2a90", fontSize: "9px", fontWeight: "700" }}>
              NEW
            </span>
          ) : null,
      },

      // clone and remove SKU buttons in case of PENDING status
      
      ...(status ==="PENDING" ? [
        {
          headerName: "",
          mappedValue: "",
          width: 35,
          renderCell: (value: any, row: any) => {
            return (
              <button
                className="view_order_manage_order_table_button"
                onClick={() => {
                  if(row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return
                  handleClone(row);
                }}
                title="Clone row"
                style={{ 
                  marginRight: "4px",
                  backgroundColor: "transparent",
                  color: "#00c6b1",
                  border: "none",
                  borderRadius: "4px",
                  width: "28px",
                  height: "28px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease-in-out"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f9f8";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <ContentCopyIcon style={{ width: "16px", height: "16px", color: "#00c6b1" }} />
              </button>
            ); 
          }
        },
        {
          headerName: "",
          mappedValue: "",
          width: 35,
          renderCell: (value: any, row: any) => {
            return (
              <button
                className="view_order_manage_order_table_button"
                onClick={() => {
                  if(row?.freeItem || row?.isChecked ||  row?.alreadySplitted) return
                  handleRemove(row);
                }}
                title="Remove row"
              >
                <SubtractIcon style={{ width: "16px", height: "16px" }} />
              </button>
            ); 
          }
        }] : []),


    ],
  };
};
